import { baseApi } from "./baseApi";

// ──────────────────────────── Types ────────────────────────────

export type PortalMemberRole =
  | "owner"
  | "admin"
  | "contributor"
  | "viewer"
  | "customer_admin"
  | "customer"
  // Legacy aliases (still accepted by backend for backward compat)
  | "manage"
  | "view"
  | "user";
export type WorkItemPriority = "critical" | "high" | "medium" | "low";
export type StatusCategory = "todo" | "in_progress" | "done";

export type WorkItemLinkType =
  | "blocks"
  | "blocked_by"
  | "relates_to"
  | "duplicates"
  | "duplicated_by"
  | "clones"
  | "is_cloned_by";

export interface Portal {
  id: string;
  instanceId: string;
  key: string;
  name: string;
  description: string | null;
  color: string;
  logoUrl: string | null;
  isPrivate: boolean;
  isArchived: boolean;
  ownerId: string;
  defaultWorkflowId: string | null;
  settings: Record<string, unknown>;
  workItemSequence: number;
  deletedAt: string | null;
  deletedBy: string | null;
  createdAt: string;
  updatedAt: string;
  memberRole?: PortalMemberRole;
  isStarred?: boolean;
  memberCount?: number;
  itemCount?: number;
}

export interface MovePortalOptions {
  targetPortal: { id: string; key: string; name: string };
  config: {
    defaultWorkflowId: string | null;
    workflows: {
      id: string;
      name: string;
      isDefault: boolean;
      statuses: {
        id: string;
        name: string;
        category: string;
        isInitial: boolean;
        position: number;
      }[];
    }[];
    requestTypes: {
      id: string;
      name: string;
      workType: string | null;
      portalGroupId: string | null;
    }[];
    portalGroups: { id: string; name: string }[];
    queues: { id: string; name: string }[];
    workTypeSettings: { workType: string; workflowId: string | null }[];
    customFieldKeys: string[];
  };
  current: {
    workType: string | null;
    statusName: string | null;
    requestTypeName: string | null;
    portalGroupName: string | null;
    queueName: string | null;
    assigneeId: string | null;
    assigneeName: string | null;
  };
  suggested: {
    workType: string | null;
    workflowId: string | null;
    statusId: string | null;
    requestTypeId: string | null;
    portalGroupId: string | null;
    queueId: string | null;
  };
  droppedCustomFieldKeys: string[];
  assigneeIsMemberOfTarget: boolean;
}

export interface MoveItemToPortalResult {
  id: string;
  itemKey: string;
  previousItemKey: string;
  targetPortalKey: string;
  droppedCustomFieldKeys: string[];
  assigneeCleared: boolean;
}

export interface PortalMember {
  id: string;
  agentId: string;
  role: PortalMemberRole;
  portalRoleId: string | null;
  permissions: Record<string, boolean>;
  isApprover: boolean;
  isStarred: boolean;
  createdAt: string;
  agentName?: string;
  agentEmail?: string;
  portalRoleName?: string;
}

export type BulkAddSkipReason = "already_member" | "inactive_agent" | "incompatible_role";

export interface BulkAddPortalMembersResult {
  added: PortalMember[];
  skipped: { agentId: string; reason: BulkAddSkipReason }[];
}

export interface PortalRole {
  id: string;
  instanceId: string;
  portalId: string | null;
  name: string;
  description: string | null;
  baseRole: PortalMemberRole;
  permissions: Record<string, boolean>;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowStatus {
  id: string;
  workflowId: string;
  name: string;
  category: StatusCategory;
  color: string;
  position: number;
  isInitial: boolean;
  isFinal: boolean;
  positionX: number | null;
  positionY: number | null;
}

export interface WorkflowTransitionRule {
  id: string;
  transitionId: string;
  ruleKind: "restrict" | "validate" | "perform";
  ruleType: string;
  config: Record<string, unknown>;
  position: number;
}

export interface WorkflowTransition {
  id: string;
  workflowId: string;
  fromStatusId: string;
  toStatusId: string;
  name: string;
  screenId: string | null;
  /** Optional editor bend point. Null when the transition uses the
   *  auto-computed default position. */
  bendX: number | null;
  bendY: number | null;
  rules?: WorkflowTransitionRule[];
}

export interface PortalWorkflow {
  id: string;
  portalId: string;
  name: string;
  isDefault: boolean;
  statuses: WorkflowStatus[];
  transitions: WorkflowTransition[];
}

export interface WorkItem {
  id: string;
  instanceId: string;
  portalId: string;
  itemNumber: number;
  itemKey: string;
  title: string;
  description: string | null;
  statusId: string | null;
  workflowId: string | null;
  requestTypeId: string | null;
  portalGroupId: string | null;
  priority: WorkItemPriority;
  assigneeId: string | null;
  reporterId: string;
  parentId: string | null;
  dueDate: string | null;
  estimatePoints: number | null;
  tags: string[];
  customFields: Record<string, unknown>;
  position: number;
  createdAt: string;
  updatedAt: string;
  // Contact & Organization
  contactId?: string | null;
  organizationId?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  organizationName?: string | null;
  // Joined fields
  statusName?: string;
  statusCategory?: StatusCategory;
  statusColor?: string;
  requestTypeName?: string;
  requestTypeIcon?: string;
  workType?: string | null;
  portalGroupName?: string;
  assigneeName?: string;
  assigneeEmail?: string;
  assignedGroupId?: string | null;
  assignedGroupName?: string | null;
  canClaim?: boolean;
  reporterName?: string;
  reporterEmail?: string;
  portalKey?: string;
  portalName?: string;
  commentCount?: number;
  mergedIntoId?: string | null;
  mergedIntoKey?: string | null;
  /** Portal of the surviving ticket — a merge can cross portals (#937). */
  mergedIntoPortalKey?: string | null;
  // Parent (when this item is a subtask) — surfaces the parent in Linked Items.
  parentItemKey?: string | null;
  parentTitle?: string | null;
  parentStatusName?: string;
  parentStatusColor?: string;
  parentStatusCategory?: StatusCategory;
  archivedAt?: string | null;
  isDeleted?: boolean;
  canEditDescription?: boolean;
  isCurrentUserReporter?: boolean;
  isCurrentUserAssignee?: boolean;
}

export interface WorkItemComment {
  id: string;
  workItemId: string;
  authorId: string;
  body: string;
  htmlBody: string | null;
  visibility: "public" | "internal";
  metadata?: {
    source?: string;
    agentId?: string;
    agentName?: string;
    [key: string]: unknown;
  } | null;
  createdAt: string;
  updatedAt: string;
  authorName?: string;
  authorEmail?: string;
  attachments?: WorkItemAttachment[];
  canEdit?: boolean;
  canDelete?: boolean;
  edited?: boolean;
}

export interface WorkItemHistory {
  id: string;
  workItemId: string;
  actorId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  fieldName: string | null;
  previousValue: string | null;
  newValue: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface WorkItemLink {
  id: string;
  sourceItemId: string;
  targetItemId: string;
  linkType: WorkItemLinkType;
  createdById: string | null;
  createdAt: string;
  linkedItemId?: string;
  linkedItemKey?: string;
  linkedItemTitle?: string;
  linkedStatusName?: string;
  linkedStatusColor?: string;
  linkedStatusCategory?: StatusCategory;
  // Legacy fields (kept for backward compat)
  targetItemKey?: string;
  targetItemTitle?: string;
  targetItemStatus?: string;
}

export interface WorkItemChild {
  id: string;
  itemKey: string;
  title: string;
  statusId: string;
  statusName?: string;
  statusColor?: string;
  statusCategory?: StatusCategory;
  priority: WorkItemPriority;
  assigneeId: string | null;
  assigneeName?: string;
}

export interface Watcher {
  id: string;
  name: string;
  email: string;
}

export interface KbCategory {
  id: string;
  instanceId: string;
  portalId: string;
  name: string;
  description: string | null;
  parentId: string | null;
  position: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  children?: KbCategory[];
}

export interface KbArticle {
  id: string;
  instanceId: string;
  portalId: string;
  title: string;
  body: string | null;
  htmlBody: string | null;
  slug: string;
  status: "draft" | "published" | "archived";
  authorId: string | null;
  publishedAt: string | null;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  tags: string[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  categoryIds?: string[];
  categoryNames?: string[];
}

export interface CsatSettings {
  id: string;
  instanceId: string;
  portalId: string;
  isEnabled: boolean;
  triggerEvent: string;
  delayMinutes: number;
  emailSubject: string | null;
  emailBody: string | null;
  scaleType: string;
  surveyType: string;
  createdAt: string;
  updatedAt: string;
}

export interface CsatResponse {
  id: string;
  instanceId: string;
  portalId: string;
  workItemId: string | null;
  token: string;
  rating: number | null;
  comment: string | null;
  respondentEmail: string | null;
  respondentName: string | null;
  surveyType: string;
  submittedAt: string | null;
  createdAt: string;
  itemKey?: string;
  itemTitle?: string;
  assigneeName?: string;
  priority?: string;
  ticketCreatedAt?: string;
}

export interface CsatStats {
  averageRating: number | null;
  totalResponses: number;
  sentCount: number;
  satisfactionRate: number | null;
  ratingDistribution: Record<number, number>;
  trendData: { date: string; avgRating: number; count: number }[];
  byAgent: {
    agentId: string;
    agentName: string;
    avgRating: number;
    count: number;
    satisfiedCount: number;
  }[];
  byPriority: { priority: string; avgRating: number; count: number }[];
}

export interface NpsStats {
  npsScore: number | null;
  totalResponses: number;
  sentCount: number;
  promoters: number;
  passives: number;
  detractors: number;
  promoterPercent: number | null;
  passivePercent: number | null;
  detractorPercent: number | null;
  ratingDistribution: Record<number, number>;
  trendData: { date: string; npsScore: number; count: number }[];
  byAgent: {
    agentId: string;
    agentName: string;
    npsScore: number;
    count: number;
    promoters: number;
    passives: number;
    detractors: number;
  }[];
}

export interface WorkItemAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  uploadedById: string;
  createdAt: string;
}

export interface WorkItemApprover {
  id: string;
  approverId: string;
  status: string;
  respondedAt: string | null;
  createdAt: string;
  agentName: string | null;
  agentEmail: string | null;
  isCurrentUser?: boolean;
}

export interface BoardColumn {
  statusId: string;
  statusName: string;
  statusCategory: StatusCategory;
  statusColor: string;
  position: number;
  items: WorkItem[];
  wipLimit?: number;
}

export interface BoardData {
  columns: BoardColumn[];
  totalItems: number;
}

export interface BoardConfig {
  id: string;
  portalId: string;
  workflowId: string | null;
  swimlaneField: string | null;
  cardFields: string[];
  wipLimits: Record<string, number>;
}

export interface RequestType {
  id: string;
  portalId: string;
  name: string;
  description: string | null;
  icon: string;
  workflowId: string | null;
  portalGroupId: string | null;
  workType: string | null;
  isActive: boolean;
  position: number;
  formSchema: Record<string, unknown>[];
  formId: string | null;
  createdAt: string;
  updatedAt: string;
  workflowName?: string;
  portalGroupName?: string;
}

/** Dependencies that block deleting a workflow (GET …/workflows/:id/usage). */
export interface WorkflowUsage {
  ticketCount: number;
  binnedTicketCount: number;
  workTypes: string[];
}

/**
 * Dependencies that block deleting an SLA policy
 * (GET …/sla-policies/:id/usage). Queues and agent groups matter as much as
 * work types: those two columns have no FK, so a delete left them holding a
 * dangling id rather than nulling it (#1056).
 */
export interface SlaPolicyUsage {
  workTypes: string[];
  queues: string[];
  agentGroups: string[];
}

export interface WorkTypeSettings {
  id: string;
  portalId: string;
  workType: string;
  slaPolicyId: string | null;
  slaPolicyName: string | null;
  workflowId: string | null;
  workflowName: string | null;
  requiresApproval: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PortalGroup {
  id: string;
  portalId: string;
  name: string;
  description: string | null;
  defaultAssigneeId: string | null;
  workType: string | null;
  position: number;
  createdAt: string;
  updatedAt: string;
  requestTypeCount?: number;
}

export interface QueueAssignmentConfig {
  mode: "off" | "round_robin" | "least_loaded" | "skill_based" | "specific" | "group";
  agentId?: string;
  groupId?: string;
  fallbackMode?: "unassigned" | "default_agent";
  fallbackAgentId?: string;
}

export interface QueueCapacityConfig {
  maxItems?: number;
  enforceAgentCapacity?: boolean;
}

export interface Queue {
  id: string;
  portalId: string;
  name: string;
  description: string | null;
  icon: string;
  filters: Record<string, unknown>;
  sortBy: string;
  sortOrder: string;
  isSystem: boolean;
  position: number;
  assignmentConfig?: QueueAssignmentConfig;
  agentGroupIds?: string[];
  capacityConfig?: QueueCapacityConfig;
  slaPolicyId?: string | null;
  businessHoursId?: string | null;
  isDefault?: boolean;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
}

export interface SavedView {
  id: string;
  portalId: string;
  agentId: string;
  name: string;
  description: string | null;
  icon: string;
  filters: {
    search?: string;
    statusId?: string;
    priority?: string;
    assigneeId?: string;
    workType?: string;
    requestTypeIds?: string[];
    customFieldFilters?: Array<{ fieldKey: string; operator: string; value?: string }>;
  };
  sortBy: string;
  sortOrder: string;
  columns: string[] | null;
  isDefault: boolean;
  position: number;
  createdAt: string;
  updatedAt: string;
}

// Instance-level Fields (global, not portal-scoped)
export interface InstanceField {
  id: string;
  fieldKey: string;
  name: string;
  description: string | null;
  fieldType:
    | "text"
    | "number"
    | "date"
    | "select"
    | "multiselect"
    | "checkbox"
    | "url"
    | "user_picker"
    | "file";
  isRequired: boolean;
  defaultValue: unknown;
  options: string[];
  validation: Record<string, unknown>;
  position: number;
  createdAt: string;
  updatedAt: string;
  usageCount: number;
}

// Custom Fields (portal-scoped, legacy)
export interface CustomField {
  id: string;
  portalId: string;
  fieldKey: string;
  name: string;
  description: string | null;
  fieldType:
    | "text"
    | "number"
    | "date"
    | "select"
    | "multiselect"
    | "checkbox"
    | "url"
    | "user_picker"
    | "asset_ref"
    | "file";
  isRequired: boolean;
  defaultValue: unknown;
  options: string[];
  validation: Record<string, unknown>;
  position: number;
  category: "general" | "resolution";
  applicableWorkTypes: string[];
  createdAt: string;
  updatedAt: string;
}

// Form Builder
export type FormStatus = "draft" | "published" | "archived";
export type FormFieldType =
  | "text"
  | "textarea"
  | "dropdown"
  | "radio"
  | "checkbox"
  | "date"
  | "file";

export interface FormField {
  id: string;
  formId: string;
  label: string;
  fieldType: FormFieldType;
  options: string[];
  isRequired: boolean;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface Form {
  id: string;
  instanceId: string;
  portalId: string;
  name: string;
  description: string | null;
  status: FormStatus;
  fields?: FormField[];
  createdAt: string;
  updatedAt: string;
  // snake_case from list endpoint (no transformer in place)
  response_count?: string;
  field_count?: string;
  created_at?: string;
  updated_at?: string;
}

export interface FormResponseAnswer {
  fieldId: string;
  fieldLabel: string;
  value: string;
}

export interface FormResponse {
  id: string;
  formId: string;
  submittedBy: string;
  submittedAt: string;
  requestTypeId: string | null;
  workItemId: string | null;
  ticketKey: string | null;
  answers: FormResponseAnswer[];
}

// SLA Policies
export interface WorkItemSla {
  id: string;
  metric: "first_response" | "resolution";
  status: "active" | "paused" | "met" | "warning" | "breached";
  targetMinutes: number;
  dueAt: string | null;
  completedAt: string | null;
  elapsedMinutes: number;
  startedAt: string;
  policyId: string | null;
}

export interface SlaTarget {
  id: string;
  slaPolicyId: string;
  priority: "critical" | "high" | "medium" | "low";
  metric: "first_response" | "resolution";
  targetMinutes: number;
}

export interface RecurringTicketTemplate {
  id: string;
  instanceId: string;
  portalId: string;
  name: string;
  title: string;
  description: string | null;
  requestTypeId: string | null;
  portalGroupId: string | null;
  priority: WorkItemPriority;
  assigneeType: "user" | "queue" | null;
  assigneeId: string | null;
  frequency: "daily" | "weekly" | "monthly" | "yearly";
  interval: number;
  weekdays: number[] | null;
  dayOfMonth: number | null;
  monthOfYear: number | null;
  runTime: string;
  timezone: string;
  active: boolean;
  startDate: string | null;
  endDate: string | null;
  nextRunAt: string;
  lastRunAt: string | null;
  lastCreatedTicketId: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export type RecurringTicketTemplateInput = Omit<
  RecurringTicketTemplate,
  | "id"
  | "instanceId"
  | "portalId"
  | "nextRunAt"
  | "lastRunAt"
  | "lastCreatedTicketId"
  | "createdBy"
  | "createdByName"
  | "createdAt"
  | "updatedAt"
>;

export interface SlaPolicy {
  id: string;
  portalId: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  targets: SlaTarget[];
  createdAt: string;
  updatedAt: string;
}

// Business Hours
export interface DaySchedule {
  day: string;
  enabled: boolean;
  startTime: string;
  endTime: string;
}

export interface Holiday {
  date: string;
  name: string;
}

export interface BusinessHoursConfig {
  id: string;
  portalId: string;
  name: string;
  timezone: string;
  isDefault: boolean;
  schedule: DaySchedule[];
  holidays: Holiday[];
  createdAt: string;
  updatedAt: string;
}

export interface AssignmentMatrixRule {
  id: string;
  instanceId: string;
  portalId: string;
  businessHoursId: string;
  agentGroupId: string;
  position: number;
  businessHoursName: string;
  businessHoursTimezone: string;
  businessHoursSchedule: Record<string, { start: string; end: string } | null>;
  businessHoursHolidays: { date: string; name: string }[];
  agentGroupName: string;
  defaultAssigneeId: string | null;
  defaultAssigneeName: string | null;
  defaultAssigneeEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AssignmentMatrixResponse {
  data: AssignmentMatrixRule[];
  total: number;
  shiftHandoffMode: string;
}

export interface CoverageBlock {
  agentGroupId: string;
  agentGroupName: string;
  businessHoursName: string;
  day: string;
  startMinutesUtc: number;
  endMinutesUtc: number;
}

export interface CoverageGap {
  day: string;
  startMinutesUtc: number;
  endMinutesUtc: number;
}

export interface CoverageData {
  blocks: CoverageBlock[];
  gaps: CoverageGap[];
}

// Automation Rules
export interface AutomationCondition {
  field: string;
  operator: "equals" | "not_equals" | "contains" | "in" | "is_empty" | "greater_than";
  value: unknown;
}

export interface AutomationAction {
  type:
    | "set_field"
    | "transition_status"
    | "assign_to"
    | "add_comment"
    | "add_tag"
    | "send_email"
    | "assign_asset";
  config: Record<string, unknown>;
}

export interface AutomationTriggerConfig {
  field?: string;
  duration?: number;
  unit?: "minutes" | "hours" | "days";
}

export type AutomationTriggerType =
  | "work_item_created"
  | "work_item_updated"
  | "status_changed"
  | "comment_added"
  | "sla_warning"
  | "sla_breached"
  | "assignment_changed"
  | "time_elapsed";

export interface AutomationRule {
  id: string;
  portalId: string;
  name: string;
  description: string | null;
  triggerType: AutomationTriggerType;
  triggerConfig: AutomationTriggerConfig;
  conditions: AutomationCondition[];
  actions: AutomationAction[];
  isActive: boolean;
  runCount: number;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Macro {
  id: string;
  portalId: string;
  name: string;
  description: string | null;
  actions: AutomationAction[];
  createdBy: string | null;
  isPersonal: boolean;
  usageCount: number;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Dashboard types
export interface PortalsDashboardSummary {
  totalOpenItems: number;
  todoCount: number;
  inProgressCount: number;
  overdueItems: number;
  /** Count of work items with an SLA row in 'warning' or 'breached' state.
   *  True count — not capped by the at-risk list's LIMIT 20. */
  atRiskItems: number;
  unassignedItems: number;
  completedInPeriod: number;
  totalPortals: number;
  activePortals: number;
  totalMembers: number;
}

export interface StatusBreakdown {
  category: string;
  count: number;
}

export interface PriorityBreakdown {
  priority: string;
  count: number;
}

export interface ItemsByPortal {
  portalName: string;
  portalKey: string;
  openCount: number;
}

export interface PortalActivity {
  portalKey: string;
  portalName: string;
  created: number;
  completed: number;
}

export interface ActivityTimeseriesPoint {
  date: string;
  created: number;
  completed: number;
  byPortal: PortalActivity[];
}

export interface AtRiskItem {
  itemKey: string;
  title: string;
  priority: string;
  dueDate: string;
  portalName: string;
  assigneeName: string | null;
  assigneeEmail: string | null;
  daysOverdue: number;
  slaStatus: "warning" | "breached";
}

export interface PortalsDashboardData {
  summary: PortalsDashboardSummary;
  statusBreakdown: StatusBreakdown[];
  priorityBreakdown: PriorityBreakdown[];
  itemsByPortal: ItemsByPortal[];
  activityTimeseries: ActivityTimeseriesPoint[];
  atRiskItems: AtRiskItem[];
}

export interface HubSpotProperty {
  name: string;
  label: string;
}

export interface HubSpotSyncFilter {
  propertyName: string;
  operator:
    | "EQ"
    | "NEQ"
    | "CONTAINS"
    | "NOT_CONTAINS"
    | "HAS_PROPERTY"
    | "NOT_HAS_PROPERTY"
    | "GT"
    | "LT"
    | "GTE"
    | "LTE";
  value?: string;
}

export interface HubSpotConfig {
  enabled: boolean;
  fieldMapping: Record<string, string>;
  syncFilters: HubSpotSyncFilter[];
  lastSyncAt: string | null;
  pushEnabled: boolean;
  noteOwnerEmail: string;
  webhookUrl: string | null;
}

export interface HubSpotSyncResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

// ── SharePoint types ──

export interface SharePointConfig {
  enabled: boolean;
  azureTenantId?: string;
  azureClientId?: string;
  siteUrl?: string;
  siteId?: string | null;
  driveId?: string | null;
  driveName?: string | null;
  folderId?: string | null;
  folderPath?: string | null;
  categoryId?: string | null;
  lastSyncAt?: string | null;
}

export interface SharePointDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

export interface SharePointFolder {
  id: string;
  name: string;
  childCount: number;
  webUrl: string;
}

export interface SharePointFilePreview {
  id: string;
  name: string;
  size: number;
  eTag: string;
  webUrl: string;
  mimeType: string;
  alreadySynced: boolean;
  unchanged: boolean;
}

export interface SharePointSyncResult {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: string[];
}

export type WebhookEvent =
  | "work_item_created"
  | "work_item_updated"
  | "status_changed"
  | "comment_added"
  | "sla_breached"
  | "assignment_changed";

export interface Webhook {
  id: string;
  instanceId: string;
  portalId: string;
  name: string;
  url: string;
  method: "POST" | "PUT";
  headers: Record<string, string>;
  events: WebhookEvent[];
  secret: string;
  isActive: boolean;
  failureCount: number;
  lastTriggeredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDeliveryLog {
  id: string;
  instanceId: string;
  webhookId: string;
  event: string;
  requestUrl: string;
  requestBody: string;
  responseCode: number | null;
  responseBody: string | null;
  durationMs: number | null;
  status: "success" | "failed" | "pending";
  retryCount: number;
  nextRetryAt: string | null;
  createdAt: string;
}

export interface HelpWidget {
  id: string;
  name: string;
  productName: string;
  apiKey: string;
  isActive: boolean;
  primaryColor: string | null;
  logoUrl: string | null;
  welcomeMessage: string | null;
  allowedOrigins: string[];
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ──────────────────────────── AI Agent Types ────────────────────────────

export interface AiAgent {
  id: string;
  instanceId: string;
  portalId: string;
  name: string;
  description: string | null;
  systemPrompt: string | null;
  welcomeMessage: string | null;
  isActive: boolean;
  useKnowledgeBase: boolean;
  enableTriage: boolean;
  enableAiEmailTriage: boolean;
  enableEscalation: boolean;
  escalationMessage: string | null;
  modelSettings: {
    model?: string;
    temperature?: number;
    maxOutputTokens?: number;
  };
  skills: Record<string, { enabled: boolean; config?: Record<string, unknown> }>;
  stats: {
    totalConversations: number;
    resolvedCount: number;
    escalatedCount: number;
  };
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  portalName?: string;
  portalKey?: string;
  portalColor?: string;
}

export interface AiAgentKbCategory {
  id: string;
  categoryId: string;
  categoryName: string;
}

export interface AiSkillProvider {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  iconUrl: string | null;
  category: "built_in" | "connected_app" | "custom";
  isConnected: boolean;
  isSystem: boolean;
  skills?: AiSkill[];
}

export interface AiSkill {
  id: string;
  providerId: string;
  providerName?: string;
  providerSlug?: string;
  name: string;
  slug: string;
  description: string | null;
  iconUrl: string | null;
  isBeta: boolean;
  isSystem: boolean;
  inputSchema: Record<string, unknown>;
  executionType: "internal" | "api_call" | "webhook";
}

export interface AiAgentSkill {
  id: string;
  skillId: string;
  skillName: string;
  skillSlug: string;
  providerName: string;
  isEnabled: boolean;
  config: Record<string, unknown>;
}

export interface AiChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AiChatResponse {
  response: string;
  sessionId: string;
  triageSuggestion?: {
    priority: string;
    suggestedRequestType: string;
    shouldEscalate: boolean;
  };
}

export interface AiConversationSummary {
  sessionId: string;
  status: string;
  isResolved: boolean;
  isEscalated: boolean;
  workItemId: string | null;
  messageCount: number;
  preview: string | null;
  createdAt: string;
  updatedAt: string;
}

// ──────────────────────────── Custom Report Types ────────────────────────────

export interface CustomReportFilter {
  field: string;
  operator: "eq" | "neq" | "in" | "not_in";
  value: string | string[];
}

export interface CustomReportConfig {
  metric: "ticket_count" | "avg_resolution_time" | "avg_csat" | "sla_compliance_rate";
  dimension: "status" | "priority" | "assignee" | "request_type" | "day" | "week" | "month";
  filters: CustomReportFilter[];
  chartType: "bar" | "line" | "pie" | "table";
  dateRange: { startDate: string; endDate: string };
}

export interface CustomReportItem {
  id: string;
  instanceId: string;
  portalId: string;
  name: string;
  description: string | null;
  config: CustomReportConfig;
  isShared: boolean;
  createdBy: string;
  position: number;
  createdAt: string;
  updatedAt: string;
}

// ──────────────────────────── Scheduled Reports ────────────────────────────

export type ScheduledReportFrequency = "daily" | "weekly" | "monthly";
export type ScheduledReportDateRange =
  | "yesterday"
  | "last_7_days"
  | "last_30_days"
  | "last_week"
  | "last_month";
export type ScheduledReportBodyFormat = "markdown" | "plain";
export type ScheduledReportType = "custom" | "builtin";
export type BuiltinReportKey =
  | "created_vs_resolved"
  | "sla_compliance"
  | "agent_performance"
  | "request_types"
  | "resolution_time"
  | "csat"
  | "nps";

export interface ScheduledReportPayload {
  reportType: ScheduledReportType;
  reportId?: string | null;
  reportKey?: BuiltinReportKey | null;
  name?: string | null;
  frequency: ScheduledReportFrequency;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  timeOfDay: string;
  timezone: string;
  dateRange: ScheduledReportDateRange;
  recipientsTo: string[];
  recipientsCc: string[];
  recipientsBcc: string[];
  subjectTemplate: string;
  bodyTemplate: string;
  bodyFormat: ScheduledReportBodyFormat;
  enabled: boolean;
}

export interface ScheduledReportRow {
  id: string;
  instance_id: string;
  portal_id: string;
  report_type: ScheduledReportType;
  report_id: string | null;
  report_key: BuiltinReportKey | null;
  name: string | null;
  frequency: ScheduledReportFrequency;
  day_of_week: number | null;
  day_of_month: number | null;
  time_of_day: string;
  timezone: string;
  date_range: ScheduledReportDateRange;
  recipients_to: string[];
  recipients_cc: string[];
  recipients_bcc: string[];
  subject_template: string;
  body_template: string;
  body_format: ScheduledReportBodyFormat;
  enabled: boolean;
  next_run_at: string;
  last_run_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ScheduledReportRunRow {
  id: string;
  started_at: string;
  completed_at: string | null;
  status: "success" | "failed";
  error: string | null;
  recipient_count: number;
  sendgrid_message_id: string | null;
}

// ──────────────────────────── API ────────────────────────────

export const portalsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Portal CRUD
    getBinPolicy: builder.query<{ retentionDays: number; purgeEnabled: boolean }, void>({
      query: () => ({ url: "/portals/bin-policy" }),
    }),

    getPortals: builder.query<
      PaginatedResponse<Portal>,
      { filter?: string; page?: number; limit?: number; search?: string }
    >({
      query: (params) => ({
        url: "/portals",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((p) => ({ type: "Portal" as const, id: p.id })),
              { type: "Portal", id: "LIST" },
            ]
          : [{ type: "Portal", id: "LIST" }],
    }),

    getMyPortals: builder.query<Portal[], void>({
      query: () => ({
        url: "/portals",
        params: { filter: "mine", limit: 100 },
      }),
      transformResponse: (response: PaginatedResponse<Portal>) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: "Portal" as const, id: p.id })),
              { type: "Portal", id: "MY_LIST" },
            ]
          : [{ type: "Portal", id: "MY_LIST" }],
    }),

    getPortal: builder.query<Portal, string>({
      query: (portalKey) => `/portals/${portalKey}`,
      providesTags: (_result, _error, portalKey) => [{ type: "Portal", id: portalKey }],
    }),

    createPortal: builder.mutation<
      Portal,
      {
        key: string;
        name: string;
        description?: string;
        color?: string;
        isPrivate?: boolean;
        template?: "it-service" | "hr-service" | "general" | "blank";
      }
    >({
      query: (body) => ({
        url: "/portals",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Portal", id: "LIST" },
        { type: "Portal", id: "MY_LIST" },
      ],
    }),

    copyPortal: builder.mutation<
      {
        targetPortalId: string;
        copied: Record<string, number>;
        reused: Record<string, number>;
        skipped: { kind: string; name: string; reason: string }[];
      },
      {
        sourcePortalId: string;
        targetInstanceId: string;
        name: string;
        key: string;
        color: string;
        modules: string[];
      }
    >({
      query: ({ sourcePortalId, ...body }) => ({
        url: `/portals/copy/${sourcePortalId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Portal", id: "LIST" },
        { type: "Portal", id: "MY_LIST" },
      ],
    }),

    updatePortal: builder.mutation<Portal, { portalKey: string; body: Record<string, unknown> }>({
      query: ({ portalKey, body }) => ({
        url: `/portals/${portalKey}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "Portal", id: portalKey },
        { type: "Portal", id: "LIST" },
      ],
    }),

    deletePortal: builder.mutation<void, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Portal", id: "LIST" },
        { type: "Portal", id: "MY_LIST" },
      ],
    }),

    archivePortal: builder.mutation<Portal, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/archive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, portalKey) => [
        { type: "Portal", id: portalKey },
        { type: "Portal", id: "LIST" },
      ],
    }),

    restorePortal: builder.mutation<void, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/restore`,
        method: "POST",
      }),
      // Refetch on 410/404 as well as on success. Both mean the Bin on screen
      // is stale — the portal expired or was already purged — so leaving the
      // card there with a working Restore button just invites the same failure
      // again.
      invalidatesTags: (_result, error) =>
        !error || error.status === 410 || error.status === 404
          ? [{ type: "Portal", id: "LIST" }]
          : [],
    }),

    permanentlyDeletePortal: builder.mutation<void, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/permanent`,
        method: "DELETE",
      }),
      // A 404 means it is already gone and a 409 means it is no longer in the
      // Bin; either way the list is out of date.
      invalidatesTags: (_result, error) =>
        !error || error.status === 404 || error.status === 409
          ? [{ type: "Portal", id: "LIST" }]
          : [],
    }),

    starPortal: builder.mutation<{ isStarred: boolean }, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/star`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, portalKey) => [
        { type: "Portal", id: portalKey },
        { type: "Portal", id: "MY_LIST" },
        { type: "Portal", id: "LIST" },
      ],
    }),

    uploadPortalLogo: builder.mutation<{ logoUrl: string }, { portalKey: string; file: File }>({
      query: ({ portalKey, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/portals/${portalKey}/logo`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "Portal", id: portalKey },
        { type: "Portal", id: "LIST" },
      ],
    }),

    deletePortalLogo: builder.mutation<{ success: boolean }, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/logo`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, portalKey) => [
        { type: "Portal", id: portalKey },
        { type: "Portal", id: "LIST" },
      ],
    }),

    // Members
    getPortalMembers: builder.query<PortalMember[], string>({
      query: (portalKey) => `/portals/${portalKey}/members`,
      transformResponse: (response: { data: PortalMember[] } | PortalMember[]) =>
        Array.isArray(response) ? response : response.data,
      providesTags: (_result, _error, portalKey) => [{ type: "PortalMember", id: portalKey }],
    }),

    addPortalMember: builder.mutation<
      PortalMember,
      {
        portalKey: string;
        agentId: string;
        role?: PortalMemberRole;
        portalRoleId?: string | null;
        isApprover?: boolean;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/members`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalMember", id: portalKey },
        { type: "Portal", id: portalKey },
      ],
    }),

    bulkAddPortalMembers: builder.mutation<
      BulkAddPortalMembersResult,
      {
        portalKey: string;
        agentIds: string[];
        role?: PortalMemberRole;
        portalRoleId?: string | null;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/members/bulk`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalMember", id: portalKey },
        { type: "Portal", id: portalKey },
      ],
    }),

    updatePortalMember: builder.mutation<
      PortalMember,
      {
        portalKey: string;
        agentId: string;
        role?: PortalMemberRole;
        portalRoleId?: string | null;
        isApprover?: boolean;
      }
    >({
      query: ({ portalKey, agentId, ...body }) => ({
        url: `/portals/${portalKey}/members/${agentId}`,
        method: "PATCH",
        body,
      }),
      // Also bust the customer-side portals cache so a role change (e.g. promoting
      // someone to customer_admin) propagates to currentUserCanReadAll without a
      // hard refresh when the same browser session straddles admin and customer.
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalMember", id: portalKey },
        { type: "Portal", id: "CUSTOMER_LIST" },
        { type: "Portal", id: `CUSTOMER_${portalKey}` },
      ],
      async onQueryStarted({ portalKey, agentId, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          portalsApi.util.updateQueryData("getPortalMembers", portalKey, (draft) => {
            const member = draft.find((m) => m.agentId === agentId);
            if (member) {
              if (patch.role !== undefined) member.role = patch.role;
              if (patch.portalRoleId !== undefined)
                member.portalRoleId = patch.portalRoleId ?? null;
              if (patch.isApprover !== undefined) member.isApprover = patch.isApprover;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    removePortalMember: builder.mutation<void, { portalKey: string; agentId: string }>({
      query: ({ portalKey, agentId }) => ({
        url: `/portals/${portalKey}/members/${agentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalMember", id: portalKey },
        { type: "Portal", id: portalKey },
      ],
    }),

    getMyPortalPermissions: builder.query<
      { role: string; effectivePermissions: Record<string, boolean> },
      string
    >({
      query: (portalKey) => `/portals/${portalKey}/my-permissions`,
      providesTags: (_result, _error, portalKey) => [{ type: "PortalMember", id: portalKey }],
    }),

    getMyTicketAccess: builder.query<
      { exists: boolean; isDeleted: boolean; canViewAsAgent: boolean; canViewAsCustomer: boolean },
      { portalKey: string; itemKey: string }
    >({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/access`,
      providesTags: (_result, _error, { portalKey, itemKey }) => [
        { type: "TicketAccess", id: `${portalKey}:${itemKey}` },
      ],
    }),

    getPortalApprovers: builder.query<PortalMember[], string>({
      query: (portalKey) => `/portals/${portalKey}/members/approvers`,
      transformResponse: (response: { data: PortalMember[] } | PortalMember[]) =>
        Array.isArray(response) ? response : response.data,
      providesTags: (_result, _error, portalKey) => [
        { type: "PortalMember", id: `approvers_${portalKey}` },
      ],
    }),

    getMemberHistory: builder.query<
      {
        id: string;
        action: string;
        fieldName: string | null;
        previousValue: string | null;
        newValue: string | null;
        actorName: string | null;
        actorEmail: string | null;
        createdAt: string;
      }[],
      { portalKey: string; agentId: string }
    >({
      query: ({ portalKey, agentId }) => `/portals/${portalKey}/members/${agentId}/history`,
      transformResponse: (response: {
        data: {
          id: string;
          action: string;
          fieldName: string | null;
          previousValue: string | null;
          newValue: string | null;
          actorName: string | null;
          actorEmail: string | null;
          createdAt: string;
        }[];
      }) => response.data,
      providesTags: (_result, _error, { portalKey, agentId }) => [
        { type: "PortalMember", id: `history_${portalKey}_${agentId}` },
      ],
    }),

    // Work Items
    getWorkItems: builder.query<
      PaginatedResponse<WorkItem>,
      {
        portalKey: string;
        statusId?: string | string[];
        assigneeId?: string;
        assignedGroupId?: string;
        requestTypeId?: string | string[];
        workType?: string;
        portalGroupId?: string | string[];
        priority?: string | string[];
        search?: string;
        dueDateFrom?: string;
        dueDateTo?: string;
        customFieldFilters?: string;
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/items`,
        params,
      }),
      providesTags: (result, _error, { portalKey }) =>
        result
          ? [
              ...result.data.map((item) => ({ type: "WorkItem" as const, id: item.itemKey })),
              { type: "WorkItem", id: `LIST_${portalKey}` },
            ]
          : [{ type: "WorkItem", id: `LIST_${portalKey}` }],
    }),

    getWorkItem: builder.query<WorkItem, { portalKey: string; itemKey: string }>({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}`,
      providesTags: (_result, _error, { itemKey }) => [{ type: "WorkItem", id: itemKey }],
    }),

    createWorkItem: builder.mutation<
      WorkItem,
      {
        portalKey: string;
        title: string;
        description?: string;
        requestTypeId?: string | null;
        portalGroupId?: string | null;
        priority?: WorkItemPriority;
        assigneeId?: string;
        reporterEmail?: string;
        reporterName?: string;
        parentId?: string;
        dueDate?: string;
        estimatePoints?: number;
        tags?: string[];
        attachmentIds?: string[];
        customFields?: Record<string, unknown>;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/items`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey, parentId }) => [
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
        { type: "WorkItem", id: `TAGS_${portalKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        ...(parentId ? [{ type: "WorkItem" as const, id: "CHILDREN_LIST" }] : []),
      ],
    }),

    updateWorkItem: builder.mutation<
      WorkItem,
      { portalKey: string; itemKey: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, itemKey, body }) => ({
        url: `/portals/${portalKey}/items/${itemKey}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey, itemKey }) => [
        { type: "WorkItem", id: itemKey },
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItemSla", id: itemKey },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "AvailableTransitions", id: itemKey },
      ],
    }),

    deleteWorkItem: builder.mutation<void, { portalKey: string; itemKey: string }>({
      query: ({ portalKey, itemKey }) => ({
        url: `/portals/${portalKey}/items/${itemKey}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey, itemKey }) => [
        { type: "WorkItem", id: itemKey },
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
        { type: "WorkItem", id: "ALL_TICKETS" },
      ],
    }),

    // Board
    getWorkItemsByBoard: builder.query<
      BoardData,
      { portalKey: string; groupBy?: string; workType?: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/board`,
        params,
      }),
      providesTags: (_result, _error, { portalKey }) => [{ type: "PortalBoard", id: portalKey }],
    }),

    moveWorkItem: builder.mutation<
      WorkItem,
      {
        portalKey: string;
        itemKey: string;
        targetStatusId: string;
        position?: number;
        workType?: string;
      }
    >({
      query: ({ portalKey, itemKey, targetStatusId, position }) => ({
        url: `/portals/${portalKey}/board/move`,
        method: "POST",
        body: { itemKey, targetStatusId, position },
      }),
      async onQueryStarted(
        { portalKey, itemKey, targetStatusId, position, workType },
        { dispatch, queryFulfilled },
      ) {
        // Optimistic update for board — cache key must match the query args
        const patchResult = dispatch(
          portalsApi.util.updateQueryData(
            "getWorkItemsByBoard",
            { portalKey, workType },
            (draft) => {
              // Find and remove item from source column
              let movedItem: WorkItem | undefined;
              for (const column of draft.columns) {
                const idx = column.items.findIndex((i) => i.itemKey === itemKey);
                if (idx >= 0) {
                  movedItem = column.items.splice(idx, 1)[0];
                  break;
                }
              }
              // Add to target column
              if (movedItem) {
                movedItem.statusId = targetStatusId;
                const targetCol = draft.columns.find((c) => c.statusId === targetStatusId);
                if (targetCol) {
                  if (position !== undefined) {
                    targetCol.items.splice(position, 0, movedItem);
                  } else {
                    targetCol.items.push(movedItem);
                  }
                }
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalBoard", id: portalKey },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "Queue", id: portalKey },
      ],
    }),

    getMovePortalOptions: builder.query<
      MovePortalOptions,
      { portalKey: string; itemKey: string; targetPortalId: string }
    >({
      query: ({ portalKey, itemKey, targetPortalId }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/move-portal/options`,
        params: { targetPortalId },
      }),
      transformResponse: (response: { data: MovePortalOptions }) => response.data,
    }),

    moveItemToPortal: builder.mutation<
      MoveItemToPortalResult,
      {
        portalKey: string;
        itemKey: string;
        targetPortalId: string;
        workflowId: string;
        statusId: string;
        requestTypeId?: string | null;
        portalGroupId?: string | null;
        queueId?: string | null;
        keepAssignee: boolean;
      }
    >({
      query: ({ portalKey, itemKey, ...body }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/move-portal`,
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: MoveItemToPortalResult }) => response.data,
      invalidatesTags: (_result, _error, { portalKey, itemKey }) => [
        { type: "WorkItem", id: itemKey },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
      ],
    }),

    getBoardConfig: builder.query<BoardConfig, string>({
      query: (portalKey) => `/portals/${portalKey}/board/config`,
      providesTags: (_result, _error, portalKey) => [
        { type: "PortalBoard", id: `config_${portalKey}` },
      ],
    }),

    updateBoardConfig: builder.mutation<
      BoardConfig,
      { portalKey: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, body }) => ({
        url: `/portals/${portalKey}/board/config`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalBoard", id: `config_${portalKey}` },
      ],
    }),

    // Approvers
    getWorkItemApprovers: builder.query<WorkItemApprover[], { portalKey: string; itemKey: string }>(
      {
        query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/approvers`,
        providesTags: (_result, _error, { itemKey }) => [
          { type: "WorkItem", id: `APPROVERS_${itemKey}` },
        ],
      },
    ),

    respondWorkItemApproval: builder.mutation<
      WorkItemApprover,
      { portalKey: string; itemKey: string; status: "approved" | "rejected"; approverId?: string }
    >({
      query: ({ portalKey, itemKey, status, approverId }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/approvers/respond`,
        method: "PATCH",
        body: { status, approverId },
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `APPROVERS_${itemKey}` },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
      ],
    }),

    addWorkItemApprovers: builder.mutation<
      WorkItemApprover[],
      { portalKey: string; itemKey: string; approverIds: string[] }
    >({
      query: ({ portalKey, itemKey, ...body }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/approvers`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `APPROVERS_${itemKey}` },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
      ],
    }),

    removeWorkItemApprover: builder.mutation<
      { success: boolean },
      { portalKey: string; itemKey: string; approverId: string }
    >({
      query: ({ portalKey, itemKey, approverId }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/approvers/${approverId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `APPROVERS_${itemKey}` },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
      ],
    }),

    // Comments
    getWorkItemComments: builder.query<WorkItemComment[], { portalKey: string; itemKey: string }>({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/comments`,
      providesTags: (_result, _error, { itemKey }) => [{ type: "WorkItemComment", id: itemKey }],
    }),

    addWorkItemComment: builder.mutation<
      WorkItemComment,
      {
        portalKey: string;
        itemKey: string;
        body: string;
        htmlBody?: string;
        visibility?: string;
        hasAttachments?: boolean;
      }
    >({
      query: ({ portalKey, itemKey, ...commentBody }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/comments`,
        method: "POST",
        body: commentBody,
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItemComment", id: itemKey },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
        { type: "WorkItemSla", id: itemKey },
      ],
    }),

    updateWorkItemComment: builder.mutation<
      { data: { id: string; metadata: Record<string, unknown> } },
      { portalKey: string; itemKey: string; commentId: string; metadata: Record<string, unknown> }
    >({
      query: ({ portalKey, itemKey, commentId, metadata }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/comments/${commentId}`,
        method: "PATCH",
        body: { metadata },
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [{ type: "WorkItemComment", id: itemKey }],
    }),

    updateWorkItemCommentBody: builder.mutation<
      { data: { id: string; body: string; html_body: string | null; updated_at: string } },
      {
        portalKey: string;
        itemKey: string;
        commentId: string;
        body: string;
        htmlBody?: string | null;
      }
    >({
      query: ({ portalKey, itemKey, commentId, body, htmlBody }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/comments/${commentId}`,
        method: "PATCH",
        body: { body, htmlBody },
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItemComment", id: itemKey },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
      ],
    }),

    deleteWorkItemComment: builder.mutation<
      void,
      { portalKey: string; itemKey: string; commentId: string }
    >({
      query: ({ portalKey, itemKey, commentId }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/comments/${commentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItemComment", id: itemKey },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
      ],
    }),

    // Workflows
    getPortalWorkflows: builder.query<PortalWorkflow[], string>({
      query: (portalKey) => `/portals/${portalKey}/workflows`,
      transformResponse: (response: PortalWorkflow[] | PortalWorkflow | null) => {
        if (Array.isArray(response)) return response;
        if (response) return [response];
        return [];
      },
      providesTags: (_result, _error, portalKey) => [{ type: "PortalWorkflow", id: portalKey }],
    }),

    // Request Types
    getRequestTypes: builder.query<
      { data: RequestType[]; total: number },
      {
        portalKey: string;
        portalGroupId?: string;
        workType?: string;
        search?: string;
        isActive?: boolean;
      }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/request-types`,
        params,
      }),
      providesTags: (_result, _error, { portalKey }) => [{ type: "RequestType", id: portalKey }],
    }),

    createRequestType: builder.mutation<
      RequestType,
      {
        portalKey: string;
        name: string;
        description?: string;
        icon?: string;
        workflowId?: string | null;
        portalGroupId?: string | null;
        workType?: string | null;
        isActive?: boolean;
        position?: number;
        formSchema?: Record<string, unknown>[];
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/request-types`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "RequestType", id: portalKey },
        { type: "RequestType", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    updateRequestType: builder.mutation<
      RequestType,
      { portalKey: string; requestTypeId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, requestTypeId, body }) => ({
        url: `/portals/${portalKey}/request-types/${requestTypeId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "RequestType", id: portalKey },
        { type: "RequestType", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    deleteRequestType: builder.mutation<void, { portalKey: string; requestTypeId: string }>({
      query: ({ portalKey, requestTypeId }) => ({
        url: `/portals/${portalKey}/request-types/${requestTypeId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "RequestType", id: portalKey },
        { type: "RequestType", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    renameWorkType: builder.mutation<
      { success: boolean; updated: number },
      { portalKey: string; oldName: string; newName: string }
    >({
      query: ({ portalKey, oldName, newName }) => ({
        url: `/portals/${portalKey}/request-types/work-types/${encodeURIComponent(oldName)}`,
        method: "PATCH",
        body: { newName },
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "RequestType", id: portalKey },
        { type: "RequestType", id: `WT_SETTINGS_${portalKey}` },
        { type: "PortalGroup", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    deleteWorkType: builder.mutation<
      { success: boolean; cleared: number },
      { portalKey: string; workTypeName: string }
    >({
      query: ({ portalKey, workTypeName }) => ({
        url: `/portals/${portalKey}/request-types/work-types/${encodeURIComponent(workTypeName)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "RequestType", id: portalKey },
        { type: "RequestType", id: `WT_SETTINGS_${portalKey}` },
        { type: "PortalGroup", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    // Work Type Settings
    getWorkTypeSettings: builder.query<{ data: WorkTypeSettings[] }, string>({
      query: (portalKey) => `/portals/${portalKey}/request-types/work-types/settings`,
      providesTags: (_result, _error, portalKey) => [
        { type: "RequestType", id: `WT_SETTINGS_${portalKey}` },
      ],
    }),

    updateWorkTypeSettings: builder.mutation<
      WorkTypeSettings,
      {
        portalKey: string;
        workTypeName: string;
        slaPolicyId?: string | null;
        workflowId?: string | null;
        requiresApproval?: boolean;
      }
    >({
      query: ({ portalKey, workTypeName, ...body }) => ({
        url: `/portals/${portalKey}/request-types/work-types/${encodeURIComponent(workTypeName)}/settings`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "RequestType", id: `WT_SETTINGS_${portalKey}` },
      ],
    }),

    // Portal Groups
    getPortalGroups: builder.query<{ data: PortalGroup[]; total: number }, string>({
      query: (portalKey) => `/portals/${portalKey}/groups`,
      providesTags: (_result, _error, portalKey) => [{ type: "PortalGroup", id: portalKey }],
    }),

    createPortalGroup: builder.mutation<
      PortalGroup,
      {
        portalKey: string;
        name: string;
        description?: string;
        defaultAssigneeId?: string | null;
        workType?: string | null;
        position?: number;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/groups`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalGroup", id: portalKey },
        { type: "PortalGroup", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    updatePortalGroup: builder.mutation<
      PortalGroup,
      { portalKey: string; groupId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, groupId, body }) => ({
        url: `/portals/${portalKey}/groups/${groupId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalGroup", id: portalKey },
        { type: "PortalGroup", id: `CUSTOMER_${portalKey}` },
        { type: "RequestType", id: portalKey },
        { type: "RequestType", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    deletePortalGroup: builder.mutation<void, { portalKey: string; groupId: string }>({
      query: ({ portalKey, groupId }) => ({
        url: `/portals/${portalKey}/groups/${groupId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalGroup", id: portalKey },
        { type: "PortalGroup", id: `CUSTOMER_${portalKey}` },
        { type: "RequestType", id: portalKey },
        { type: "RequestType", id: `CUSTOMER_${portalKey}` },
        { type: "Portal", id: "CUSTOMER_LIST" },
      ],
    }),

    updateGroupRequestTypes: builder.mutation<
      { success: boolean },
      { portalKey: string; groupId: string; requestTypeIds: string[] }
    >({
      query: ({ portalKey, groupId, requestTypeIds }) => ({
        url: `/portals/${portalKey}/groups/${groupId}/request-types`,
        method: "PUT",
        body: { requestTypeIds },
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalGroup", id: portalKey },
        { type: "RequestType", id: portalKey },
      ],
    }),

    reorderPortalGroup: builder.mutation<
      PortalGroup[],
      { portalKey: string; groupIdA: string; groupIdB: string }
    >({
      query: ({ portalKey, groupIdA, groupIdB }) => ({
        url: `/portals/${portalKey}/groups/reorder`,
        method: "POST",
        body: { groupIdA, groupIdB },
      }),
      async onQueryStarted({ portalKey, groupIdA, groupIdB }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          portalsApi.util.updateQueryData("getPortalGroups", portalKey, (draft) => {
            // Normalize positions first (handles all-zero / duplicate positions)
            draft.data.forEach((g, i) => {
              g.position = i;
            });
            const idxA = draft.data.findIndex((g) => g.id === groupIdA);
            const idxB = draft.data.findIndex((g) => g.id === groupIdB);
            if (idxA < 0 || idxB < 0) return;
            const tempPos = draft.data[idxA].position;
            draft.data[idxA].position = draft.data[idxB].position;
            draft.data[idxB].position = tempPos;
            draft.data.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalGroup", id: `CUSTOMER_${portalKey}` },
        { type: "RequestType", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    reorderRequestType: builder.mutation<
      RequestType[],
      { portalKey: string; rtIdA: string; rtIdB: string }
    >({
      query: ({ portalKey, rtIdA, rtIdB }) => ({
        url: `/portals/${portalKey}/request-types/reorder`,
        method: "POST",
        body: { rtIdA, rtIdB },
      }),
      async onQueryStarted({ portalKey, rtIdA, rtIdB }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          portalsApi.util.updateQueryData("getRequestTypes", { portalKey }, (draft) => {
            // Normalize positions first (handles all-zero / duplicate positions)
            draft.data.forEach((rt, i) => {
              rt.position = i;
            });
            const idxA = draft.data.findIndex((rt) => rt.id === rtIdA);
            const idxB = draft.data.findIndex((rt) => rt.id === rtIdB);
            if (idxA < 0 || idxB < 0) return;
            const tempPos = draft.data[idxA].position;
            draft.data[idxA].position = draft.data[idxB].position;
            draft.data[idxB].position = tempPos;
            draft.data.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "RequestType", id: `CUSTOMER_${portalKey}` },
      ],
    }),

    // Queues
    getQueues: builder.query<
      { data: Queue[]; total: number },
      { portalKey: string; requestTypeIds?: string[] }
    >({
      query: ({ portalKey, requestTypeIds }) => ({
        url: `/portals/${portalKey}/queues`,
        params: requestTypeIds?.length ? { requestTypeIds: requestTypeIds.join(",") } : undefined,
      }),
      providesTags: (_result, _error, { portalKey }) => [{ type: "Queue", id: portalKey }],
    }),

    getQueueItems: builder.query<
      PaginatedResponse<WorkItem>,
      {
        portalKey: string;
        queueId: string;
        search?: string;
        customFieldFilters?: string;
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
        requestTypeIds?: string[];
        statusId?: string;
        priority?: string;
        assigneeId?: string;
      }
    >({
      query: ({ portalKey, queueId, requestTypeIds, ...params }) => ({
        url: `/portals/${portalKey}/queues/${queueId}/items`,
        params: {
          ...params,
          ...(requestTypeIds?.length ? { requestTypeIds: requestTypeIds.join(",") } : {}),
        },
      }),
      providesTags: (_result, _error, { portalKey, queueId }) => [
        { type: "Queue", id: `items_${portalKey}_${queueId}` },
        { type: "WorkItem", id: `LIST_${portalKey}` },
      ],
    }),

    createQueue: builder.mutation<
      Queue,
      {
        portalKey: string;
        name: string;
        description?: string;
        icon?: string;
        filters?: Record<string, unknown>;
        sortBy?: string;
        sortOrder?: string;
        position?: number;
        assignmentConfig?: QueueAssignmentConfig;
        agentGroupIds?: string[];
        capacityConfig?: QueueCapacityConfig;
        slaPolicyId?: string | null;
        businessHoursId?: string | null;
        isDefault?: boolean;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/queues`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Queue", id: portalKey }],
    }),

    updateQueue: builder.mutation<
      Queue,
      { portalKey: string; queueId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, queueId, body }) => ({
        url: `/portals/${portalKey}/queues/${queueId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Queue", id: portalKey }],
    }),

    deleteQueue: builder.mutation<void, { portalKey: string; queueId: string }>({
      query: ({ portalKey, queueId }) => ({
        url: `/portals/${portalKey}/queues/${queueId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Queue", id: portalKey }],
    }),

    // Portal Roles
    getPortalRoles: builder.query<{ data: PortalRole[]; total: number }, string>({
      query: (portalKey) => `/portals/${portalKey}/roles`,
      providesTags: (_result, _error, portalKey) => [{ type: "PortalRole", id: portalKey }],
    }),

    // ── Saved Views ─────────────────────────────────────────
    getSavedViews: builder.query<{ data: SavedView[] }, string>({
      query: (portalKey) => `/portals/${portalKey}/views`,
      providesTags: (_r, _e, portalKey) => [{ type: "SavedView", id: portalKey }],
    }),

    createSavedView: builder.mutation<
      { data: SavedView },
      { portalKey: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, body }) => ({
        url: `/portals/${portalKey}/views`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "SavedView", id: portalKey }],
    }),

    updateSavedView: builder.mutation<
      { data: SavedView },
      { portalKey: string; viewId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, viewId, body }) => ({
        url: `/portals/${portalKey}/views/${viewId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "SavedView", id: portalKey }],
    }),

    deleteSavedView: builder.mutation<void, { portalKey: string; viewId: string }>({
      query: ({ portalKey, viewId }) => ({
        url: `/portals/${portalKey}/views/${viewId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "SavedView", id: portalKey }],
    }),

    createPortalRole: builder.mutation<
      PortalRole,
      {
        portalKey: string;
        name: string;
        description?: string | null;
        baseRole?: PortalMemberRole;
        portalId?: string | null;
        permissions?: Record<string, boolean>;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/roles`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "PortalRole", id: portalKey }],
    }),

    updatePortalRole: builder.mutation<
      PortalRole,
      {
        portalKey: string;
        roleId: string;
        body: {
          name?: string;
          description?: string | null;
          baseRole?: PortalMemberRole;
          permissions?: Record<string, boolean>;
        };
      }
    >({
      query: ({ portalKey, roleId, body }) => ({
        url: `/portals/${portalKey}/roles/${roleId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "PortalRole", id: portalKey }],
    }),

    deletePortalRole: builder.mutation<void, { portalKey: string; roleId: string }>({
      query: ({ portalKey, roleId }) => ({
        url: `/portals/${portalKey}/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "PortalRole", id: portalKey }],
    }),

    // Workflow mutations
    createWorkflow: builder.mutation<
      PortalWorkflow,
      { portalKey: string; name: string; isDefault?: boolean }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/workflows`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    // What still depends on a workflow — drives the warning in the delete
    // dialog. Always refetched when the dialog opens (never trust a cached
    // count for a destructive action).
    getWorkflowUsage: builder.query<WorkflowUsage, { portalKey: string; workflowId: string }>({
      query: ({ portalKey, workflowId }) => `/portals/${portalKey}/workflows/${workflowId}/usage`,
    }),

    deleteWorkflow: builder.mutation<void, { portalKey: string; workflowId: string }>({
      query: ({ portalKey, workflowId }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    createWorkflowStatus: builder.mutation<
      WorkflowStatus,
      {
        portalKey: string;
        workflowId: string;
        name: string;
        category: StatusCategory;
        color?: string;
        position?: number;
        isInitial?: boolean;
        isFinal?: boolean;
      }
    >({
      query: ({ portalKey, workflowId, ...body }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/statuses`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    updateWorkflowStatus: builder.mutation<
      WorkflowStatus,
      { portalKey: string; workflowId: string; statusId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, workflowId, statusId, body }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/statuses/${statusId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    reorderWorkflowStatus: builder.mutation<
      WorkflowStatus[],
      {
        portalKey: string;
        workflowId: string;
        statusIdA: string;
        statusIdB: string;
      }
    >({
      query: ({ portalKey, workflowId, statusIdA, statusIdB }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/statuses/reorder`,
        method: "POST",
        body: { statusIdA, statusIdB },
      }),
      async onQueryStarted(
        { portalKey, workflowId, statusIdA, statusIdB },
        { dispatch, queryFulfilled },
      ) {
        // Optimistic update — swap statuses in cache immediately
        const patchResult = dispatch(
          portalsApi.util.updateQueryData("getPortalWorkflows", portalKey, (draft) => {
            const wf = draft.find((w) => w.id === workflowId);
            if (!wf?.statuses) return;
            const idxA = wf.statuses.findIndex((s) => s.id === statusIdA);
            const idxB = wf.statuses.findIndex((s) => s.id === statusIdB);
            if (idxA < 0 || idxB < 0) return;
            // Swap positions and array order
            const tempPos = wf.statuses[idxA].position;
            wf.statuses[idxA].position = wf.statuses[idxB].position;
            wf.statuses[idxB].position = tempPos;
            wf.statuses.sort((a, b) => a.position - b.position);
          }),
        );
        try {
          const { data: updated } = await queryFulfilled;
          // Apply server-confirmed positions to the cache (no refetch needed)
          dispatch(
            portalsApi.util.updateQueryData("getPortalWorkflows", portalKey, (draft) => {
              const wf = draft.find((w) => w.id === workflowId);
              if (!wf?.statuses) return;
              for (const serverStatus of updated) {
                const local = wf.statuses.find((s) => s.id === serverStatus.id);
                if (local) local.position = serverStatus.position;
              }
              wf.statuses.sort((a, b) => a.position - b.position);
            }),
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    getStatusTicketCount: builder.query<
      { count: number },
      { portalKey: string; workflowId: string; statusId: string }
    >({
      query: ({ portalKey, workflowId, statusId }) =>
        `/portals/${portalKey}/workflows/${workflowId}/statuses/${statusId}/ticket-count`,
    }),

    deleteWorkflowStatus: builder.mutation<
      void,
      { portalKey: string; workflowId: string; statusId: string }
    >({
      query: ({ portalKey, workflowId, statusId }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/statuses/${statusId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    createWorkflowTransition: builder.mutation<
      WorkflowTransition,
      { portalKey: string; workflowId: string; fromStatusId?: string | null; toStatusId: string }
    >({
      query: ({ portalKey, workflowId, ...body }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/transitions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    deleteWorkflowTransition: builder.mutation<
      void,
      { portalKey: string; workflowId: string; transitionId: string }
    >({
      query: ({ portalKey, workflowId, transitionId }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/transitions/${transitionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    createTransition: builder.mutation<
      WorkflowTransition,
      {
        portalKey: string;
        workflowId: string;
        body: { name: string; fromStatusId: string; toStatusId: string };
      }
    >({
      query: ({ portalKey, workflowId, body }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/transitions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "PortalWorkflow", id: portalKey }],
    }),

    updateTransition: builder.mutation<
      WorkflowTransition,
      {
        portalKey: string;
        workflowId: string;
        transitionId: string;
        body: {
          name?: string;
          /** Send a number to set, null to clear back to auto-default. */
          bendX?: number | null;
          bendY?: number | null;
        };
      }
    >({
      query: ({ portalKey, workflowId, transitionId, body }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/transitions/${transitionId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "PortalWorkflow", id: portalKey }],
    }),

    deleteTransition: builder.mutation<
      void,
      { portalKey: string; workflowId: string; transitionId: string }
    >({
      query: ({ portalKey, workflowId, transitionId }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/transitions/${transitionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "PortalWorkflow", id: portalKey }],
    }),

    listTransitionRules: builder.query<
      WorkflowTransitionRule[],
      { portalKey: string; workflowId: string; transitionId: string }
    >({
      query: ({ portalKey, workflowId, transitionId }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/transitions/${transitionId}/rules`,
        method: "GET",
      }),
      providesTags: (_r, _e, { transitionId }) => [{ type: "TransitionRule", id: transitionId }],
    }),

    createTransitionRule: builder.mutation<
      WorkflowTransitionRule,
      {
        portalKey: string;
        workflowId: string;
        transitionId: string;
        body: {
          ruleKind: "restrict" | "perform";
          ruleType: string;
          config: Record<string, unknown>;
          position?: number;
        };
      }
    >({
      query: ({ portalKey, workflowId, transitionId, body }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/transitions/${transitionId}/rules`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { transitionId, portalKey }) => [
        { type: "TransitionRule", id: transitionId },
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    updateTransitionRule: builder.mutation<
      WorkflowTransitionRule,
      {
        portalKey: string;
        workflowId: string;
        transitionId: string;
        ruleId: string;
        body: { config?: Record<string, unknown>; position?: number };
      }
    >({
      query: ({ portalKey, workflowId, transitionId, ruleId, body }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/transitions/${transitionId}/rules/${ruleId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { transitionId, portalKey }) => [
        { type: "TransitionRule", id: transitionId },
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    deleteTransitionRule: builder.mutation<
      void,
      { portalKey: string; workflowId: string; transitionId: string; ruleId: string }
    >({
      query: ({ portalKey, workflowId, transitionId, ruleId }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/transitions/${transitionId}/rules/${ruleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { transitionId, portalKey }) => [
        { type: "TransitionRule", id: transitionId },
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    // ───── Status Entry Rules ─────
    // Rules attached to a STATUS that fire on every transition into it.
    // Same shape as transition rules; just keyed by status_id instead.
    listStatusEntryRules: builder.query<
      WorkflowTransitionRule[],
      { portalKey: string; workflowId: string; statusId: string }
    >({
      query: ({ portalKey, workflowId, statusId }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/statuses/${statusId}/entry-rules`,
        method: "GET",
      }),
      providesTags: (_r, _e, { statusId }) => [{ type: "StatusEntryRule", id: statusId }],
    }),

    createStatusEntryRule: builder.mutation<
      WorkflowTransitionRule,
      {
        portalKey: string;
        workflowId: string;
        statusId: string;
        body: {
          ruleKind: "restrict" | "perform";
          ruleType: string;
          config: Record<string, unknown>;
          position?: number;
        };
      }
    >({
      query: ({ portalKey, workflowId, statusId, body }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/statuses/${statusId}/entry-rules`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { statusId, portalKey }) => [
        { type: "StatusEntryRule", id: statusId },
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    updateStatusEntryRule: builder.mutation<
      WorkflowTransitionRule,
      {
        portalKey: string;
        workflowId: string;
        statusId: string;
        ruleId: string;
        body: { config?: Record<string, unknown>; position?: number };
      }
    >({
      query: ({ portalKey, workflowId, statusId, ruleId, body }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/statuses/${statusId}/entry-rules/${ruleId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { statusId, portalKey }) => [
        { type: "StatusEntryRule", id: statusId },
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    deleteStatusEntryRule: builder.mutation<
      void,
      { portalKey: string; workflowId: string; statusId: string; ruleId: string }
    >({
      query: ({ portalKey, workflowId, statusId, ruleId }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/statuses/${statusId}/entry-rules/${ruleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { statusId, portalKey }) => [
        { type: "StatusEntryRule", id: statusId },
        { type: "PortalWorkflow", id: portalKey },
      ],
    }),

    bulkSetStatusPositions: builder.mutation<
      void,
      {
        portalKey: string;
        workflowId: string;
        positions: { statusId: string; x: number; y: number }[];
      }
    >({
      query: ({ portalKey, workflowId, positions }) => ({
        url: `/portals/${portalKey}/workflows/${workflowId}/statuses/bulk-positions`,
        method: "POST",
        body: { positions },
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "PortalWorkflow", id: portalKey }],
    }),

    getAvailableTransitions: builder.query<
      {
        id: string;
        name: string;
        toStatusId: string;
        toStatusName: string;
        blocked: boolean;
        failures: {
          type: string;
          message: string;
          /** Present when type === "restrict.field_condition" — the
           *  field key/label referenced by the failing rule. Used by
           *  the UI to open a dialog scoped to the specific fields
           *  that need to be filled for the transition to unblock. */
          fieldKey?: string;
          fieldLabel?: string;
          isCustomField?: boolean;
          source?: "transition" | "entry";
        }[];
      }[],
      { portalKey: string; itemKey: string }
    >({
      query: ({ portalKey, itemKey }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/available-transitions`,
        method: "GET",
      }),
      providesTags: (_r, _e, { itemKey }) => [{ type: "AvailableTransitions", id: itemKey }],
    }),

    // All portal items (shared across Backlog, Calendar views)
    getAllPortalItems: builder.query<WorkItem[], string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/items`,
        params: { limit: 500, sortBy: "created_at", sortOrder: "desc" },
      }),
      transformResponse: (response: PaginatedResponse<WorkItem>) => response.data,
      providesTags: (result, _error, portalKey) =>
        result
          ? [
              ...result.map((item) => ({ type: "WorkItem" as const, id: item.itemKey })),
              { type: "WorkItem", id: `ALL_${portalKey}` },
            ]
          : [{ type: "WorkItem", id: `ALL_${portalKey}` }],
    }),

    getWorkItemCounts: builder.query<{ total: number; byWorkType: Record<string, number> }, string>(
      {
        query: (portalKey) => `/portals/${portalKey}/items/counts`,
        providesTags: (_result, _error, portalKey) => [
          { type: "WorkItem", id: `ALL_${portalKey}` },
        ],
      },
    ),

    // My Work (cross-portal)
    getMyWorkItems: builder.query<
      PaginatedResponse<WorkItem>,
      { status?: string; sortBy?: string; sortOrder?: string; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: "/portals/my-work",
        params,
      }),
      providesTags: [{ type: "WorkItem", id: "MY_WORK" }],
    }),

    // All Tickets (cross-portal, all items not just assigned)
    getAllTickets: builder.query<
      PaginatedResponse<WorkItem>,
      {
        status?: string;
        search?: string;
        priority?: string | string[];
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: "/portals/all-tickets",
        params,
      }),
      providesTags: [{ type: "WorkItem", id: "ALL_TICKETS" }],
    }),

    // Portal Email Config
    getPortalEmailConfig: builder.query<
      {
        emailConfig: {
          inboundPrefix: string | null;
          outboundFromName: string | null;
          isEnabled: boolean;
        };
        domain: string;
        fullInboundEmail: string | null;
      },
      string
    >({
      query: (portalKey) => `/portals/${portalKey}/email-config`,
      providesTags: (_r, _e, portalKey) => [{ type: "Portal", id: `email_${portalKey}` }],
    }),

    updatePortalEmailConfig: builder.mutation<
      {
        emailConfig: {
          inboundPrefix: string | null;
          outboundFromName: string | null;
          isEnabled: boolean;
        };
        domain: string;
        fullInboundEmail: string | null;
      },
      {
        portalKey: string;
        body: {
          inboundPrefix?: string | null;
          outboundFromName?: string | null;
          isEnabled?: boolean;
        };
      }
    >({
      query: ({ portalKey, body }) => ({
        url: `/portals/${portalKey}/email-config`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [
        { type: "Portal", id: `email_${portalKey}` },
        { type: "Portal", id: portalKey },
      ],
    }),

    // Notification Preferences (per role or agent group)
    getNotificationPreferences: builder.query<
      {
        type: "role" | "group";
        id: string;
        preferences: Record<string, { inProduct: boolean; email: boolean }>;
        configured: boolean;
      },
      { portalKey: string; type: "role" | "group"; id: string }
    >({
      query: ({ portalKey, type, id }) => ({
        url: `/portals/${portalKey}/notification-preferences`,
        params: { type, id },
      }),
      providesTags: (_r, _e, { portalKey, type, id }) => [
        { type: "Portal", id: `notifprefs_${portalKey}_${type}_${id}` },
      ],
    }),

    updateNotificationPreferences: builder.mutation<
      {
        type: "role" | "group";
        id: string;
        preferences: Record<string, { inProduct: boolean; email: boolean }>;
      },
      {
        portalKey: string;
        type: "role" | "group";
        id: string;
        preferences: Record<string, { inProduct: boolean; email: boolean }>;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/notification-preferences`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { portalKey, type, id }) => [
        { type: "Portal", id: `notifprefs_${portalKey}_${type}_${id}` },
      ],
    }),

    // Notification Logs
    getNotificationLogs: builder.query<
      {
        data: Array<{
          id: string;
          instanceId: string;
          portalId: string;
          workItemId: string | null;
          type: string;
          channel: string;
          recipient: string;
          subject: string | null;
          sendgridMessageId: string | null;
          status: string;
          statusDetail: string | null;
          sentAt: string;
          deliveredAt: string | null;
          metadata: Record<string, unknown> | null;
          itemKey?: string | null;
          itemTitle?: string | null;
        }>;
        total: number;
        page: number;
        limit: number;
      },
      {
        portalKey: string;
        page?: number;
        limit?: number;
        status?: string;
        type?: string;
        from?: string;
        to?: string;
        search?: string;
      }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/notification-logs`,
        params,
      }),
      providesTags: (_r, _e, { portalKey }) => [{ type: "NotificationLog", id: portalKey }],
    }),

    // Dashboard
    getPortalsDashboard: builder.query<PortalsDashboardData, { period?: string }>({
      query: (params) => ({
        url: "/portals/dashboard",
        params,
      }),
      providesTags: [{ type: "Portal", id: "DASHBOARD" }],
    }),

    // Unlinked attachment upload/delete (for work item creation flow)
    uploadPortalAttachment: builder.mutation<
      {
        data: {
          id: string;
          fileName: string;
          fileSize: number;
          contentType: string;
          createdAt: string;
        };
      },
      { portalKey: string; file: File }
    >({
      query: ({ portalKey, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/portals/${portalKey}/items/upload-attachment`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [{ type: "WorkItem", id: "LIST" }],
    }),

    deletePortalAttachment: builder.mutation<
      { success: boolean },
      { portalKey: string; attachmentId: string }
    >({
      query: ({ portalKey, attachmentId }) => ({
        url: `/portals/${portalKey}/items/upload-attachment/${attachmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "WorkItem", id: "LIST" }],
    }),

    // Attachments
    getWorkItemAttachments: builder.query<
      { data: WorkItemAttachment[] },
      { portalKey: string; itemKey: string }
    >({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/attachments`,
      providesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `ATTACHMENTS_${itemKey}` },
      ],
    }),

    uploadWorkItemAttachment: builder.mutation<
      { data: WorkItemAttachment },
      { portalKey: string; itemKey: string; file: File; commentId?: string }
    >({
      query: ({ portalKey, itemKey, file, commentId }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (commentId) {
          formData.append("commentId", commentId);
        }
        return {
          url: `/portals/${portalKey}/items/${itemKey}/attachments`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `ATTACHMENTS_${itemKey}` },
        { type: "WorkItemComment", id: itemKey },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
      ],
    }),

    deleteWorkItemAttachment: builder.mutation<
      { success: boolean },
      { portalKey: string; itemKey: string; attachmentId: string }
    >({
      query: ({ portalKey, itemKey, attachmentId }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/attachments/${attachmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `ATTACHMENTS_${itemKey}` },
      ],
    }),

    // Tags
    getPortalTags: builder.query<string[], string>({
      query: (portalKey) => `/portals/${portalKey}/items/tags`,
      providesTags: (_result, _error, portalKey) => [{ type: "WorkItem", id: `TAGS_${portalKey}` }],
    }),

    // Work Item History
    getWorkItemHistory: builder.query<WorkItemHistory[], { portalKey: string; itemKey: string }>({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/history`,
      transformResponse: (
        response: { data: WorkItemHistory[]; total: number } | WorkItemHistory[],
      ) => (Array.isArray(response) ? response : response.data),
      providesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
      ],
    }),

    // Work Item Links
    getWorkItemLinks: builder.query<WorkItemLink[], { portalKey: string; itemKey: string }>({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/links`,
      transformResponse: (response: { data: WorkItemLink[]; total: number }) => response.data,
      providesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `LINKS_${itemKey}` },
      ],
    }),

    createWorkItemLink: builder.mutation<
      WorkItemLink,
      { portalKey: string; itemKey: string; targetItemKey: string; linkType: WorkItemLinkType }
    >({
      query: ({ portalKey, itemKey, ...body }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/links`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `LINKS_${itemKey}` },
      ],
    }),

    deleteWorkItemLink: builder.mutation<
      void,
      { portalKey: string; itemKey: string; linkId: string }
    >({
      query: ({ portalKey, itemKey, linkId }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/links/${linkId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `LINKS_${itemKey}` },
      ],
    }),

    // Work Item Children (Subtasks)
    getWorkItemChildren: builder.query<WorkItemChild[], { portalKey: string; itemKey: string }>({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/children`,
      transformResponse: (response: { data: WorkItemChild[] } | WorkItemChild[]) =>
        Array.isArray(response) ? response : response.data,
      providesTags: (_result, _error, { itemKey }) => [
        { type: "WorkItem", id: `CHILDREN_${itemKey}` },
        { type: "WorkItem", id: "CHILDREN_LIST" },
      ],
    }),

    // Watchers
    getWorkItemAiReply: builder.query<string[], { portalKey: string; itemKey: string }>({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/ai-reply`,
      transformResponse: (response: { suggestions: string[] }) => response.suggestions,
    }),

    getWorkItemSummary: builder.query<
      string,
      { portalKey: string; itemKey: string; force?: boolean }
    >({
      query: ({ portalKey, itemKey, force }) =>
        `/portals/${portalKey}/items/${itemKey}/summary${force ? "?force=true" : ""}`,
      transformResponse: (response: { summary: string }) => response.summary,
    }),

    getWorkItemWatchers: builder.query<Watcher[], { portalKey: string; itemKey: string }>({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/watchers`,
      transformResponse: (response: { data: Watcher[] } | Watcher[]) =>
        Array.isArray(response) ? response : response.data,
      providesTags: (_result, _error, { itemKey }) => [{ type: "Watcher", id: itemKey }],
    }),

    addWorkItemWatcher: builder.mutation<
      void,
      { portalKey: string; itemKey: string; agentId: string }
    >({
      query: ({ portalKey, itemKey, ...body }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/watchers`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [{ type: "Watcher", id: itemKey }],
    }),

    removeWorkItemWatcher: builder.mutation<
      void,
      { portalKey: string; itemKey: string; agentId: string }
    >({
      query: ({ portalKey, itemKey, agentId }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/watchers/${agentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { itemKey }) => [{ type: "Watcher", id: itemKey }],
    }),

    // Knowledge Base - Categories
    getKbCategories: builder.query<KbCategory[], { portalKey: string; tree?: boolean }>({
      query: ({ portalKey, tree }) =>
        `/portals/${portalKey}/kb/categories${tree ? "?tree=true" : ""}`,
      transformResponse: (response: { data: KbCategory[] } | KbCategory[]) =>
        Array.isArray(response) ? response : response.data,
      providesTags: (_result, _error, { portalKey }) => [{ type: "KbCategory", id: portalKey }],
    }),

    createKbCategory: builder.mutation<
      KbCategory,
      {
        portalKey: string;
        name: string;
        description?: string;
        parentId?: string;
        position?: number;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/kb/categories`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "KbCategory", id: portalKey }],
    }),

    updateKbCategory: builder.mutation<
      KbCategory,
      { portalKey: string; categoryId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, categoryId, body }) => ({
        url: `/portals/${portalKey}/kb/categories/${categoryId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "KbCategory", id: portalKey }],
    }),

    deleteKbCategory: builder.mutation<void, { portalKey: string; categoryId: string }>({
      query: ({ portalKey, categoryId }) => ({
        url: `/portals/${portalKey}/kb/categories/${categoryId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "KbCategory", id: portalKey }],
    }),

    // Knowledge Base - Articles
    getKbArticles: builder.query<
      { data: KbArticle[]; total: number },
      {
        portalKey: string;
        status?: string;
        categoryId?: string;
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/kb/articles`,
        params,
      }),
      providesTags: (_result, _error, { portalKey }) => [{ type: "KbArticle", id: portalKey }],
    }),

    getKbArticle: builder.query<KbArticle, { portalKey: string; articleId: string }>({
      query: ({ portalKey, articleId }) => `/portals/${portalKey}/kb/articles/${articleId}`,
      transformResponse: (response: { data: KbArticle } | KbArticle) =>
        "data" in response ? response.data : response,
      providesTags: (_result, _error, { articleId }) => [{ type: "KbArticle", id: articleId }],
    }),

    createKbArticle: builder.mutation<
      KbArticle,
      {
        portalKey: string;
        title: string;
        body?: string;
        htmlBody?: string;
        slug: string;
        status?: string;
        categoryIds?: string[];
        tags?: string[];
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/kb/articles`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "KbArticle", id: portalKey }],
    }),

    updateKbArticle: builder.mutation<
      KbArticle,
      { portalKey: string; articleId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, articleId, body }) => ({
        url: `/portals/${portalKey}/kb/articles/${articleId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey, articleId }) => [
        { type: "KbArticle", id: portalKey },
        { type: "KbArticle", id: articleId },
      ],
    }),

    deleteKbArticle: builder.mutation<void, { portalKey: string; articleId: string }>({
      query: ({ portalKey, articleId }) => ({
        url: `/portals/${portalKey}/kb/articles/${articleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "KbArticle", id: portalKey }],
    }),

    publishKbArticle: builder.mutation<KbArticle, { portalKey: string; articleId: string }>({
      query: ({ portalKey, articleId }) => ({
        url: `/portals/${portalKey}/kb/articles/${articleId}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { portalKey, articleId }) => [
        { type: "KbArticle", id: portalKey },
        { type: "KbArticle", id: articleId },
      ],
    }),

    uploadKbImage: builder.mutation<{ url: string }, { portalKey: string; file: File }>({
      query: ({ portalKey, file }) => {
        const formData = new FormData();
        formData.append("image", file);
        return {
          url: `/portals/${portalKey}/kb/images`,
          method: "POST",
          body: formData,
        };
      },
    }),

    submitKbFeedback: builder.mutation<
      void,
      { portalKey: string; articleId: string; helpful: boolean }
    >({
      query: ({ portalKey, articleId, helpful }) => ({
        url: `/portals/${portalKey}/kb/articles/${articleId}/feedback`,
        method: "POST",
        body: { helpful },
      }),
    }),

    searchKbArticles: builder.query<KbArticle[], { portalKey: string; q: string; limit?: number }>({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/kb/search`,
        params,
      }),
      transformResponse: (response: { data: KbArticle[] } | KbArticle[]) =>
        Array.isArray(response) ? response : response.data,
    }),

    suggestKbArticles: builder.query<KbArticle[], { portalKey: string; q: string }>({
      query: ({ portalKey, q }) => `/portals/${portalKey}/kb/suggest?q=${encodeURIComponent(q)}`,
      transformResponse: (response: { data: KbArticle[] } | KbArticle[]) =>
        Array.isArray(response) ? response : response.data,
    }),

    // CSAT / NPS - Settings
    getCsatSettings: builder.query<CsatSettings | null, { portalKey: string; surveyType?: string }>(
      {
        query: ({ portalKey, surveyType }) => ({
          url: `/portals/${portalKey}/csat/settings`,
          params: { surveyType: surveyType ?? "csat" },
        }),
        transformResponse: (response: { data: CsatSettings | null }) => response.data,
        providesTags: (_result, _error, { portalKey, surveyType }) => [
          { type: "CsatSettings", id: `${portalKey}-${surveyType ?? "csat"}` },
        ],
      },
    ),

    updateCsatSettings: builder.mutation<
      CsatSettings,
      { portalKey: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, body }) => ({
        url: `/portals/${portalKey}/csat/settings`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey, body }) => [
        {
          type: "CsatSettings",
          id: `${portalKey}-${(body as { surveyType?: string }).surveyType ?? "csat"}`,
        },
      ],
    }),

    // CSAT - Responses
    getCsatResponses: builder.query<
      { data: CsatResponse[]; total: number },
      { portalKey: string; page?: number; limit?: number; from?: string; to?: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/csat/responses`,
        params,
      }),
      providesTags: (_result, _error, { portalKey }) => [{ type: "CsatResponse", id: portalKey }],
    }),

    getCsatStats: builder.query<CsatStats, { portalKey: string; from?: string; to?: string }>({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/csat/stats`,
        params,
      }),
      transformResponse: (response: { data: CsatStats }) => response.data,
    }),

    // NPS Stats
    getNpsStats: builder.query<NpsStats, { portalKey: string; from?: string; to?: string }>({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/csat/nps-stats`,
        params,
      }),
      transformResponse: (response: { data: NpsStats }) => response.data,
    }),

    // Help Widgets
    getHelpWidgets: builder.query<HelpWidget[], string>({
      query: (portalKey) => `/portals/${portalKey}/widgets`,
      transformResponse: (response: { data: HelpWidget[] }) => response.data,
      providesTags: (_result, _error, portalKey) => [{ type: "HelpWidget", id: portalKey }],
    }),

    createHelpWidget: builder.mutation<
      HelpWidget,
      {
        portalKey: string;
        name: string;
        productName: string;
        primaryColor?: string;
        logoUrl?: string | null;
        welcomeMessage?: string | null;
        allowedOrigins?: string[];
        isActive?: boolean;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/widgets`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "HelpWidget", id: portalKey }],
    }),

    updateHelpWidget: builder.mutation<
      HelpWidget,
      { portalKey: string; widgetId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, widgetId, body }) => ({
        url: `/portals/${portalKey}/widgets/${widgetId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "HelpWidget", id: portalKey }],
    }),

    deleteHelpWidget: builder.mutation<void, { portalKey: string; widgetId: string }>({
      query: ({ portalKey, widgetId }) => ({
        url: `/portals/${portalKey}/widgets/${widgetId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "HelpWidget", id: portalKey }],
    }),

    regenerateHelpWidgetKey: builder.mutation<HelpWidget, { portalKey: string; widgetId: string }>({
      query: ({ portalKey, widgetId }) => ({
        url: `/portals/${portalKey}/widgets/${widgetId}/regenerate-key`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "HelpWidget", id: portalKey }],
    }),

    // Bulk Operations
    bulkTransitionWorkItems: builder.mutation<
      { success: boolean; transitionedCount: number; totalRequested: number },
      { portalKey: string; itemKeys: string[]; statusId: string }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/items/bulk/transition`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey, itemKeys }) => [
        { type: "WorkItem", id: portalKey },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "PortalBoard", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
        ...itemKeys.map((key) => ({ type: "WorkItemSla" as const, id: key })),
      ],
    }),

    // Custom Fields
    getCustomFields: builder.query<
      { data: CustomField[]; total: number },
      string | { portalKey: string; category?: string }
    >({
      query: (arg) => {
        const portalKey = typeof arg === "string" ? arg : arg.portalKey;
        const category = typeof arg === "string" ? undefined : arg.category;
        const params = category ? `?category=${category}` : "";
        return `/portals/${portalKey}/custom-fields${params}`;
      },
      providesTags: (_result, _error, arg) => {
        const portalKey = typeof arg === "string" ? arg : arg.portalKey;
        return [{ type: "CustomField", id: portalKey }];
      },
      keepUnusedDataFor: 300, // 5 min cache — custom fields rarely change
    }),

    getResolutionRequirements: builder.query<
      { data: CustomField[]; total: number },
      { portalKey: string; workType?: string; targetCategory: string }
    >({
      query: ({ portalKey, workType, targetCategory }) => {
        const params = new URLSearchParams({ targetCategory });
        if (workType) params.set("workType", workType);
        return `/portals/${portalKey}/custom-fields/resolution-requirements?${params.toString()}`;
      },
      providesTags: (_result, _error, { portalKey }) => [{ type: "CustomField", id: portalKey }],
    }),

    createCustomField: builder.mutation<
      CustomField,
      {
        portalKey: string;
        fieldKey: string;
        name: string;
        description?: string;
        fieldType: CustomField["fieldType"];
        isRequired?: boolean;
        defaultValue?: unknown;
        options?: string[];
        position?: number;
        category?: string;
        applicableWorkTypes?: string[];
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/custom-fields`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "CustomField", id: portalKey }],
    }),

    updateCustomField: builder.mutation<
      CustomField,
      { portalKey: string; fieldId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, fieldId, body }) => ({
        url: `/portals/${portalKey}/custom-fields/${fieldId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "CustomField", id: portalKey }],
    }),

    deleteCustomField: builder.mutation<void, { portalKey: string; fieldId: string }>({
      query: ({ portalKey, fieldId }) => ({
        url: `/portals/${portalKey}/custom-fields/${fieldId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "CustomField", id: portalKey }],
    }),

    // SLA Policies
    getSlaPolicies: builder.query<{ data: SlaPolicy[]; total: number }, string>({
      query: (portalKey) => `/portals/${portalKey}/sla-policies`,
      providesTags: (_result, _error, portalKey) => [{ type: "SlaPolicy", id: portalKey }],
    }),

    createSlaPolicy: builder.mutation<
      SlaPolicy,
      {
        portalKey: string;
        name: string;
        description?: string;
        isDefault?: boolean;
        isActive?: boolean;
        targets?: Array<{ priority: string; metric: string; targetMinutes: number }>;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/sla-policies`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "SlaPolicy", id: portalKey }],
    }),

    updateSlaPolicy: builder.mutation<
      SlaPolicy,
      { portalKey: string; policyId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, policyId, body }) => ({
        url: `/portals/${portalKey}/sla-policies/${policyId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "SlaPolicy", id: portalKey }],
    }),

    // Preflight for the delete dialog. Always refetched when the dialog opens
    // (never trust a cached count for a destructive action).
    getSlaPolicyUsage: builder.query<SlaPolicyUsage, { portalKey: string; policyId: string }>({
      query: ({ portalKey, policyId }) => `/portals/${portalKey}/sla-policies/${policyId}/usage`,
    }),

    deleteSlaPolicy: builder.mutation<void, { portalKey: string; policyId: string }>({
      query: ({ portalKey, policyId }) => ({
        url: `/portals/${portalKey}/sla-policies/${policyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "SlaPolicy", id: portalKey }],
    }),

    // Recurring Tickets
    listRecurringTickets: builder.query<RecurringTicketTemplate[], string>({
      query: (portalKey) => `/portals/${portalKey}/recurring-tickets`,
      transformResponse: (r: { data: RecurringTicketTemplate[] }) => r.data,
      providesTags: (_r, _e, portalKey) => [{ type: "RecurringTickets" as const, id: portalKey }],
    }),
    createRecurringTicket: builder.mutation<
      RecurringTicketTemplate,
      { portalKey: string; body: RecurringTicketTemplateInput }
    >({
      query: ({ portalKey, body }) => ({
        url: `/portals/${portalKey}/recurring-tickets`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "RecurringTickets", id: portalKey }],
    }),
    updateRecurringTicket: builder.mutation<
      RecurringTicketTemplate,
      { portalKey: string; id: string; body: Partial<RecurringTicketTemplateInput> }
    >({
      query: ({ portalKey, id, body }) => ({
        url: `/portals/${portalKey}/recurring-tickets/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "RecurringTickets", id: portalKey }],
    }),
    deleteRecurringTicket: builder.mutation<void, { portalKey: string; id: string }>({
      query: ({ portalKey, id }) => ({
        url: `/portals/${portalKey}/recurring-tickets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "RecurringTickets", id: portalKey }],
    }),
    pauseRecurringTicket: builder.mutation<
      RecurringTicketTemplate,
      { portalKey: string; id: string }
    >({
      query: ({ portalKey, id }) => ({
        url: `/portals/${portalKey}/recurring-tickets/${id}/pause`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "RecurringTickets", id: portalKey }],
    }),
    resumeRecurringTicket: builder.mutation<
      RecurringTicketTemplate,
      { portalKey: string; id: string }
    >({
      query: ({ portalKey, id }) => ({
        url: `/portals/${portalKey}/recurring-tickets/${id}/resume`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "RecurringTickets", id: portalKey }],
    }),
    runRecurringTicketNow: builder.mutation<
      { ticketId: string },
      { portalKey: string; id: string }
    >({
      query: ({ portalKey, id }) => ({
        url: `/portals/${portalKey}/recurring-tickets/${id}/run-now`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { portalKey }) => [{ type: "RecurringTickets", id: portalKey }],
    }),

    // Work Item SLA Status
    getWorkItemSla: builder.query<WorkItemSla[], { portalKey: string; itemKey: string }>({
      query: ({ portalKey, itemKey }) => `/portals/${portalKey}/items/${itemKey}/sla`,
      transformResponse: (response: { data: WorkItemSla[] } | WorkItemSla[]) =>
        Array.isArray(response) ? response : response.data,
      providesTags: (_result, _error, { itemKey }) => [{ type: "WorkItemSla", id: itemKey }],
    }),

    // Business Hours
    getBusinessHours: builder.query<{ data: BusinessHoursConfig[]; total: number }, string>({
      query: (portalKey) => `/portals/${portalKey}/business-hours`,
      providesTags: (_result, _error, portalKey) => [{ type: "BusinessHours", id: portalKey }],
    }),

    createBusinessHours: builder.mutation<
      BusinessHoursConfig,
      {
        portalKey: string;
        name: string;
        timezone: string;
        isDefault?: boolean;
        schedule?: DaySchedule[];
        holidays?: Holiday[];
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/business-hours`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "BusinessHours", id: portalKey },
      ],
    }),

    updateBusinessHours: builder.mutation<
      BusinessHoursConfig,
      { portalKey: string; configId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, configId, body }) => ({
        url: `/portals/${portalKey}/business-hours/${configId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "BusinessHours", id: portalKey },
      ],
    }),

    deleteBusinessHours: builder.mutation<void, { portalKey: string; configId: string }>({
      query: ({ portalKey, configId }) => ({
        url: `/portals/${portalKey}/business-hours/${configId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "BusinessHours", id: portalKey },
      ],
    }),

    // Assignment Matrix
    getAssignmentMatrixRules: builder.query<AssignmentMatrixResponse, string>({
      query: (portalKey) => `/portals/${portalKey}/assignment-matrix`,
      providesTags: (_result, _error, portalKey) => [{ type: "AssignmentMatrix", id: portalKey }],
    }),
    createAssignmentMatrixRule: builder.mutation<
      AssignmentMatrixRule,
      { portalKey: string; businessHoursId: string; agentGroupId: string; position?: number }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/assignment-matrix`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AssignmentMatrix", id: portalKey },
      ],
    }),
    updateAssignmentMatrixRule: builder.mutation<
      AssignmentMatrixRule,
      { portalKey: string; ruleId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, ruleId, body }) => ({
        url: `/portals/${portalKey}/assignment-matrix/${ruleId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AssignmentMatrix", id: portalKey },
      ],
    }),
    deleteAssignmentMatrixRule: builder.mutation<void, { portalKey: string; ruleId: string }>({
      query: ({ portalKey, ruleId }) => ({
        url: `/portals/${portalKey}/assignment-matrix/${ruleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AssignmentMatrix", id: portalKey },
      ],
    }),
    updateShiftHandoff: builder.mutation<
      { shiftHandoffMode: string },
      { portalKey: string; shiftHandoffMode: "stay" | "reroute" }
    >({
      query: ({ portalKey, shiftHandoffMode }) => ({
        url: `/portals/${portalKey}/assignment-matrix/handoff`,
        method: "PATCH",
        body: { shiftHandoffMode },
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AssignmentMatrix", id: portalKey },
      ],
    }),
    getAssignmentMatrixCoverage: builder.query<CoverageData, string>({
      query: (portalKey) => `/portals/${portalKey}/assignment-matrix/coverage`,
      providesTags: (_result, _error, portalKey) => [{ type: "AssignmentMatrix", id: portalKey }],
    }),

    // Automation Rules
    getAutomationRules: builder.query<{ data: AutomationRule[]; total: number }, string>({
      query: (portalKey) => `/portals/${portalKey}/automations`,
      providesTags: (_result, _error, portalKey) => [{ type: "AutomationRule", id: portalKey }],
    }),

    createAutomationRule: builder.mutation<
      AutomationRule,
      {
        portalKey: string;
        name: string;
        description?: string;
        triggerType: AutomationTriggerType;
        triggerConfig?: AutomationTriggerConfig;
        conditions?: AutomationCondition[];
        actions?: AutomationAction[];
        isActive?: boolean;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/automations`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AutomationRule", id: portalKey },
      ],
    }),

    updateAutomationRule: builder.mutation<
      AutomationRule,
      { portalKey: string; ruleId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, ruleId, body }) => ({
        url: `/portals/${portalKey}/automations/${ruleId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AutomationRule", id: portalKey },
      ],
    }),

    deleteAutomationRule: builder.mutation<void, { portalKey: string; ruleId: string }>({
      query: ({ portalKey, ruleId }) => ({
        url: `/portals/${portalKey}/automations/${ruleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AutomationRule", id: portalKey },
      ],
    }),

    // ──────────────── Macros ────────────────

    getMacros: builder.query<{ data: Macro[] }, string>({
      query: (portalKey) => `/portals/${portalKey}/macros`,
      providesTags: (_result, _error, portalKey) => [{ type: "Macro", id: portalKey }],
    }),

    createMacro: builder.mutation<
      Macro,
      {
        portalKey: string;
        name: string;
        description?: string;
        actions?: AutomationAction[];
        isPersonal?: boolean;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/macros`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Macro", id: portalKey }],
    }),

    updateMacro: builder.mutation<
      Macro,
      { portalKey: string; macroId: string; body: Record<string, unknown> }
    >({
      query: ({ portalKey, macroId, body }) => ({
        url: `/portals/${portalKey}/macros/${macroId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Macro", id: portalKey }],
    }),

    deleteMacro: builder.mutation<void, { portalKey: string; macroId: string }>({
      query: ({ portalKey, macroId }) => ({
        url: `/portals/${portalKey}/macros/${macroId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Macro", id: portalKey }],
    }),

    applyMacro: builder.mutation<
      { success: boolean; results: { type: string; success: boolean; error?: string }[] },
      { portalKey: string; itemKey: string; macroId: string }
    >({
      query: ({ portalKey, itemKey, macroId }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/apply-macro`,
        method: "POST",
        body: { macroId },
      }),
      invalidatesTags: (_result, _error, { portalKey, itemKey }) => [
        { type: "WorkItem", id: itemKey },
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "WorkItemComment", id: itemKey },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
        { type: "Macro", id: portalKey },
      ],
    }),

    // ──────────────── Reports ────────────────

    getCreatedVsResolved: builder.query<
      {
        data: { date: string; created: number; resolved: number }[];
        totals: { created: number; resolved: number; backlog: number };
        allTimeTotals: { created: number; resolved: number; backlog: number };
        byPriority: { priority: string; created: number; resolved: number }[];
      },
      { portalKey: string; startDate: string; endDate: string; groupBy?: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/created-vs-resolved`,
        params,
      }),
    }),

    getSlaCompliance: builder.query<
      {
        data: { priority: string; met: number; breached: number }[];
        totals: { met: number; breached: number; complianceRate: number };
        trend: { date: string; complianceRate: number }[];
      },
      { portalKey: string; startDate: string; endDate: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/sla-compliance`,
        params,
      }),
    }),

    getAgentPerformance: builder.query<
      {
        data: {
          agentId: string;
          agentName: string | null;
          agentEmail: string;
          assigned: number;
          resolved: number;
          avgResolutionTimeHours: number;
          slaMet: number;
        }[];
      },
      { portalKey: string; startDate: string; endDate: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/agent-performance`,
        params,
      }),
    }),

    getRequestTypeBreakdown: builder.query<
      {
        data: {
          requestTypeId: string;
          requestTypeName: string;
          created: number;
          resolved: number;
          resolutionRate: number;
        }[];
      },
      { portalKey: string; startDate: string; endDate: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/request-types`,
        params,
      }),
    }),

    getResolutionTime: builder.query<
      {
        data: { date: string; avgHours: number; medianHours: number }[];
        totals: { avgHours: number; medianHours: number; totalResolved: number };
        byPriority: { priority: string; avgHours: number; medianHours: number; count: number }[];
      },
      { portalKey: string; startDate: string; endDate: string; groupBy?: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/resolution-time`,
        params,
      }),
    }),

    // ── Report Exports ──
    exportCreatedVsResolved: builder.query<
      string,
      { portalKey: string; startDate: string; endDate: string; groupBy?: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/created-vs-resolved/export`,
        params,
        responseHandler: (response) => response.text(),
      }),
    }),

    exportSlaCompliance: builder.query<
      string,
      { portalKey: string; startDate: string; endDate: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/sla-compliance/export`,
        params,
        responseHandler: (response) => response.text(),
      }),
    }),

    exportAgentPerformance: builder.query<
      string,
      { portalKey: string; startDate: string; endDate: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/agent-performance/export`,
        params,
        responseHandler: (response) => response.text(),
      }),
    }),

    exportRequestTypes: builder.query<
      string,
      { portalKey: string; startDate: string; endDate: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/request-types/export`,
        params,
        responseHandler: (response) => response.text(),
      }),
    }),

    exportResolutionTime: builder.query<
      string,
      { portalKey: string; startDate: string; endDate: string; groupBy?: string }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/reports/resolution-time/export`,
        params,
        responseHandler: (response) => response.text(),
      }),
    }),

    exportCustomReport: builder.query<string, { portalKey: string; reportId: string }>({
      query: ({ portalKey, reportId }) => ({
        url: `/portals/${portalKey}/reports/custom/${reportId}/export`,
        responseHandler: (response) => response.text(),
      }),
    }),

    // ── Custom Reports ──
    getCustomReports: builder.query<{ data: CustomReportItem[] }, { portalKey: string }>({
      query: ({ portalKey }) => `/portals/${portalKey}/reports/custom`,
      providesTags: ["CustomReport"],
    }),

    getCustomReportById: builder.query<CustomReportItem, { portalKey: string; reportId: string }>({
      query: ({ portalKey, reportId }) => `/portals/${portalKey}/reports/custom/${reportId}`,
      providesTags: ["CustomReport"],
    }),

    createCustomReport: builder.mutation<
      CustomReportItem,
      {
        portalKey: string;
        name: string;
        description?: string;
        config: CustomReportConfig;
        isShared?: boolean;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/reports/custom`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CustomReport"],
    }),

    updateCustomReport: builder.mutation<
      CustomReportItem,
      {
        portalKey: string;
        reportId: string;
        name?: string;
        description?: string;
        config?: CustomReportConfig;
        isShared?: boolean;
      }
    >({
      query: ({ portalKey, reportId, ...body }) => ({
        url: `/portals/${portalKey}/reports/custom/${reportId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["CustomReport"],
    }),

    deleteCustomReport: builder.mutation<
      { success: boolean },
      { portalKey: string; reportId: string }
    >({
      query: ({ portalKey, reportId }) => ({
        url: `/portals/${portalKey}/reports/custom/${reportId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomReport"],
    }),

    reorderCustomReports: builder.mutation<
      { success: boolean },
      { portalKey: string; order: { id: string; position: number }[] }
    >({
      query: ({ portalKey, order }) => ({
        url: `/portals/${portalKey}/reports/custom/reorder`,
        method: "PUT",
        body: { order },
      }),
      invalidatesTags: ["CustomReport"],
    }),

    executeCustomReport: builder.mutation<
      { data: { label: string; value: number }[] },
      { portalKey: string; config: CustomReportConfig }
    >({
      query: ({ portalKey, config }) => ({
        url: `/portals/${portalKey}/reports/custom/execute`,
        method: "POST",
        body: { config },
      }),
    }),

    // ── Scheduled Reports ──
    getScheduledReports: builder.query<
      { data: ScheduledReportRow[] },
      { portalKey: string; reportId?: string }
    >({
      query: ({ portalKey, reportId }) => ({
        url: `/portals/${portalKey}/reports/schedules`,
        params: reportId ? { reportId } : undefined,
      }),
      providesTags: ["ScheduledReport"],
    }),

    getScheduledReportById: builder.query<
      ScheduledReportRow,
      { portalKey: string; scheduleId: string }
    >({
      query: ({ portalKey, scheduleId }) => `/portals/${portalKey}/reports/schedules/${scheduleId}`,
      providesTags: ["ScheduledReport"],
    }),

    createScheduledReport: builder.mutation<
      ScheduledReportRow,
      { portalKey: string; payload: ScheduledReportPayload }
    >({
      query: ({ portalKey, payload }) => ({
        url: `/portals/${portalKey}/reports/schedules`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ScheduledReport"],
    }),

    updateScheduledReport: builder.mutation<
      ScheduledReportRow,
      { portalKey: string; scheduleId: string; payload: ScheduledReportPayload }
    >({
      query: ({ portalKey, scheduleId, payload }) => ({
        url: `/portals/${portalKey}/reports/schedules/${scheduleId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["ScheduledReport"],
    }),

    deleteScheduledReport: builder.mutation<void, { portalKey: string; scheduleId: string }>({
      query: ({ portalKey, scheduleId }) => ({
        url: `/portals/${portalKey}/reports/schedules/${scheduleId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ScheduledReport"],
    }),

    runScheduledReportNow: builder.mutation<
      { success: boolean; error?: string },
      { portalKey: string; scheduleId: string }
    >({
      query: ({ portalKey, scheduleId }) => ({
        url: `/portals/${portalKey}/reports/schedules/${scheduleId}/run-now`,
        method: "POST",
      }),
      invalidatesTags: ["ScheduledReport", "ScheduledReportRun"],
    }),

    getScheduledReportRuns: builder.query<
      { data: ScheduledReportRunRow[] },
      { portalKey: string; scheduleId: string }
    >({
      query: ({ portalKey, scheduleId }) =>
        `/portals/${portalKey}/reports/schedules/${scheduleId}/runs`,
      providesTags: ["ScheduledReportRun"],
    }),

    previewScheduledReport: builder.mutation<
      { subject: string; html: string; text: string },
      { portalKey: string; payload: ScheduledReportPayload }
    >({
      query: ({ portalKey, payload }) => ({
        url: `/portals/${portalKey}/reports/schedules/preview`,
        method: "POST",
        body: payload,
      }),
    }),

    sendScheduledReportTest: builder.mutation<
      { success: boolean; messageId?: string; error?: string },
      { portalKey: string; testTo: string; schedule: ScheduledReportPayload }
    >({
      query: ({ portalKey, testTo, schedule }) => ({
        url: `/portals/${portalKey}/reports/schedules/preview/send-test`,
        method: "POST",
        body: { testTo, schedule },
      }),
    }),

    // Report Builder endpoints
    generateReportConfig: builder.mutation<
      { config: import("../../../shared/schema/reportConfig").ReportConfig; explanation: string },
      {
        portalKey: string;
        message: string;
        currentConfig?: import("../../../shared/schema/reportConfig").ReportConfig;
        conversationHistory?: { role: "user" | "assistant"; content: string }[];
        formId?: string;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/reports/builder/generate`,
        method: "POST",
        body,
      }),
    }),

    executeReportConfig: builder.mutation<
      { blocks: import("../../../shared/schema/reportConfig").BlockResult[] },
      {
        portalKey: string;
        config: import("../../../shared/schema/reportConfig").ReportConfig;
        dateRange: { startDate: string; endDate: string };
        formId?: string;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/reports/builder/execute`,
        method: "POST",
        body,
      }),
    }),

    // Cached version of executeReportConfig for viewing saved reports
    executeReportConfigCached: builder.query<
      { blocks: import("../../../shared/schema/reportConfig").BlockResult[] },
      {
        portalKey: string;
        config: import("../../../shared/schema/reportConfig").ReportConfig;
        dateRange: { startDate: string; endDate: string };
        formId?: string;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/reports/builder/execute`,
        method: "POST",
        body,
      }),
      keepUnusedDataFor: 300,
    }),

    // Report Settings endpoints
    getReportSettings: builder.query<
      {
        data: {
          id: string;
          reportKey: string;
          reportType: string;
          isVisible: boolean;
          position: number;
        }[];
        prebuiltReports: { key: string; name: string }[];
      },
      { portalKey: string }
    >({
      query: ({ portalKey }) => `/portals/${portalKey}/settings/reports`,
      providesTags: ["ReportSettings"],
    }),

    updateReportVisibility: builder.mutation<
      { success: boolean },
      { portalKey: string; reportKey: string; reportType: string; visible: boolean }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/settings/reports/visibility`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ReportSettings"],
    }),

    reorderReports: builder.mutation<
      { success: boolean },
      { portalKey: string; order: { id: string; type: string; position: number }[] }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/settings/reports/reorder`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ReportSettings"],
    }),

    // ── Instance-level Custom Fields (global, not portal-scoped) ──
    getInstanceFields: builder.query<{ data: InstanceField[]; total: number }, void>({
      query: () => `/fields`,
      providesTags: ["InstanceField"],
    }),

    createInstanceField: builder.mutation<
      InstanceField,
      {
        fieldKey: string;
        name: string;
        description?: string;
        fieldType: InstanceField["fieldType"];
        isRequired?: boolean;
        defaultValue?: unknown;
        options?: string[];
        position?: number;
      }
    >({
      query: (body) => ({
        url: `/fields`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["InstanceField"],
    }),

    updateInstanceField: builder.mutation<
      InstanceField,
      { fieldId: string; body: Record<string, unknown> }
    >({
      query: ({ fieldId, body }) => ({
        url: `/fields/${fieldId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["InstanceField"],
    }),

    deleteInstanceField: builder.mutation<void, string>({
      query: (fieldId) => ({
        url: `/fields/${fieldId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["InstanceField"],
    }),

    // ── Inbox (Unified Agent Inbox) ──
    getInbox: builder.query<
      PaginatedResponse<WorkItem>,
      {
        scope?: "mine" | "all";
        portalId?: string;
        status?: string;
        priority?: string | string[];
        search?: string;
        sortBy?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: "/portals/inbox",
        params,
      }),
      providesTags: [{ type: "WorkItem", id: "INBOX" }],
    }),

    // ── Archive / Unarchive ──
    archiveWorkItem: builder.mutation<{ success: boolean }, { portalKey: string; itemKey: string }>(
      {
        query: ({ portalKey, itemKey }) => ({
          url: `/portals/${portalKey}/items/${itemKey}/archive`,
          method: "POST",
        }),
        invalidatesTags: (_result, _error, { portalKey, itemKey }) => [
          { type: "WorkItem", id: itemKey },
          { type: "WorkItem", id: `LIST_${portalKey}` },
          { type: "WorkItem", id: `ALL_${portalKey}` },
          { type: "WorkItem", id: "ALL_TICKETS" },
          { type: "WorkItem", id: "MY_WORK" },
          { type: "WorkItem", id: "INBOX" },
          { type: "PortalBoard", id: portalKey },
          { type: "Queue", id: portalKey },
        ],
      },
    ),

    unarchiveWorkItem: builder.mutation<
      { success: boolean },
      { portalKey: string; itemKey: string }
    >({
      query: ({ portalKey, itemKey }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/unarchive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { portalKey, itemKey }) => [
        { type: "WorkItem", id: itemKey },
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "WorkItem", id: "MY_WORK" },
        { type: "WorkItem", id: "INBOX" },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
      ],
    }),

    bulkArchiveWorkItems: builder.mutation<
      { success: boolean; archived: number; skipped: number },
      { portalKey: string; itemIds: string[] }
    >({
      query: ({ portalKey, itemIds }) => ({
        url: `/portals/${portalKey}/items/bulk-archive`,
        method: "POST",
        body: { itemIds },
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "WorkItem", id: "MY_WORK" },
        { type: "WorkItem", id: "INBOX" },
        { type: "WorkItem", id: "ARCHIVED" },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
      ],
    }),

    bulkDeleteWorkItems: builder.mutation<
      { success: boolean; deleted: number },
      { portalKey: string; itemIds: string[] }
    >({
      query: ({ portalKey, itemIds }) => ({
        url: `/portals/${portalKey}/items/bulk-delete`,
        method: "POST",
        body: { itemIds },
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "WorkItem", id: "MY_WORK" },
        { type: "WorkItem", id: "INBOX" },
        { type: "WorkItem", id: "DELETED" },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
      ],
    }),

    // ── Bulk Unarchive ──
    bulkUnarchiveWorkItems: builder.mutation<
      { success: boolean; unarchived: number },
      { portalKey: string; itemIds: string[] }
    >({
      query: ({ portalKey, itemIds }) => ({
        url: `/portals/${portalKey}/items/bulk-unarchive`,
        method: "POST",
        body: { itemIds },
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "WorkItem", id: "MY_WORK" },
        { type: "WorkItem", id: "INBOX" },
        { type: "WorkItem", id: "ARCHIVED" },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
      ],
    }),

    // ── List Archived Work Items ──
    getArchivedWorkItems: builder.query<
      {
        data: WorkItem[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
      },
      { portalKey: string; search?: string; page?: number; limit?: number }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/items/archived`,
        params,
      }),
      providesTags: (_result, _error, { portalKey }) => [
        { type: "WorkItem", id: "ARCHIVED" },
        { type: "WorkItem", id: `LIST_${portalKey}` },
      ],
    }),

    // ── List Deleted Work Items ──
    getDeletedWorkItems: builder.query<
      {
        data: WorkItem[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
      },
      { portalKey: string; search?: string; page?: number; limit?: number }
    >({
      query: ({ portalKey, ...params }) => ({
        url: `/portals/${portalKey}/items/deleted`,
        params,
      }),
      providesTags: (_result, _error, { portalKey }) => [
        { type: "WorkItem", id: "DELETED" },
        { type: "WorkItem", id: `LIST_${portalKey}` },
      ],
    }),

    // ── Restore All Deleted Work Items ──
    restoreAllWorkItems: builder.mutation<
      { success: boolean; restored: number },
      { portalKey: string }
    >({
      query: ({ portalKey }) => ({
        url: `/portals/${portalKey}/items/restore-all`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "WorkItem", id: "MY_WORK" },
        { type: "WorkItem", id: "INBOX" },
        { type: "WorkItem", id: "DELETED" },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
      ],
    }),

    // ── Empty Bin (Permanent Delete All) ──
    emptyBin: builder.mutation<{ success: boolean; deleted: number }, { portalKey: string }>({
      query: ({ portalKey }) => ({
        url: `/portals/${portalKey}/items/empty-bin`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "WorkItem", id: "DELETED" },
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "Portal", id: "DASHBOARD" },
      ],
    }),

    // ── Bulk Restore Deleted Work Items ──
    bulkRestoreWorkItems: builder.mutation<
      { success: boolean; restored: number },
      { portalKey: string; itemIds: string[] }
    >({
      query: ({ portalKey, itemIds }) => ({
        url: `/portals/${portalKey}/items/bulk-restore`,
        method: "POST",
        body: { itemIds },
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "WorkItem", id: `LIST_${portalKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "WorkItem", id: "MY_WORK" },
        { type: "WorkItem", id: "INBOX" },
        { type: "WorkItem", id: "DELETED" },
        { type: "PortalBoard", id: portalKey },
        { type: "Queue", id: portalKey },
        { type: "Portal", id: "DASHBOARD" },
      ],
    }),

    // ── Split Work Item ──
    splitWorkItem: builder.mutation<
      { data: { id: string; itemKey: string; title: string; commentsMoved: number } },
      {
        portalKey: string;
        itemKey: string;
        body: {
          title: string;
          description?: string | null;
          commentIds?: string[];
          priority?: string;
          assigneeId?: string | null;
        };
      }
    >({
      query: ({ portalKey, itemKey, body }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/split`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "WorkItem", id: "INBOX" },
        { type: "PortalBoard", id: portalKey },
      ],
    }),

    // ── Merge Work Item ──
    mergeWorkItem: builder.mutation<
      {
        data: {
          targetItemKey: string;
          targetPortalKey: string;
          commentsMoved: number;
          attachmentsMoved: number;
        };
      },
      { portalKey: string; itemKey: string; targetItemKey: string }
    >({
      query: ({ portalKey, itemKey, targetItemKey }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/merge`,
        method: "POST",
        body: { targetItemKey },
      }),
      // Both tickets change: the source becomes merged + closed, the target
      // gains the copied conversation. Invalidating only the lists left the
      // open ticket showing pre-merge state until a hard refresh (#937).
      invalidatesTags: (result, _error, { portalKey, itemKey }) => {
        const targetKey = result?.data.targetItemKey;
        return [
          { type: "WorkItem" as const, id: itemKey },
          { type: "WorkItemComment" as const, id: itemKey },
          { type: "WorkItem" as const, id: `HISTORY_${itemKey}` },
          { type: "WorkItem" as const, id: `LINKS_${itemKey}` },
          ...(targetKey
            ? [
                { type: "WorkItem" as const, id: targetKey },
                { type: "WorkItemComment" as const, id: targetKey },
                { type: "WorkItem" as const, id: `ATTACHMENTS_${targetKey}` },
                { type: "WorkItem" as const, id: `HISTORY_${targetKey}` },
                { type: "WorkItem" as const, id: `LINKS_${targetKey}` },
              ]
            : []),
          { type: "WorkItem" as const, id: `ALL_${portalKey}` },
          { type: "WorkItem" as const, id: "ALL_TICKETS" },
          { type: "WorkItem" as const, id: "INBOX" },
          { type: "PortalBoard" as const, id: portalKey },
        ];
      },
    }),

    // ── Unmerge Work Item ──
    unmergeWorkItem: builder.mutation<
      { data: { itemKey: string; unmerged: boolean } },
      { portalKey: string; itemKey: string }
    >({
      query: ({ portalKey, itemKey }) => ({
        url: `/portals/${portalKey}/items/${itemKey}/merge`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey, itemKey }) => [
        { type: "WorkItem", id: itemKey },
        { type: "WorkItem", id: `HISTORY_${itemKey}` },
        { type: "WorkItem", id: `LINKS_${itemKey}` },
        { type: "WorkItem", id: `ALL_${portalKey}` },
        { type: "WorkItem", id: "ALL_TICKETS" },
        { type: "PortalBoard", id: portalKey },
      ],
    }),

    // ── Webhooks ──
    getWebhooks: builder.query<Webhook[], string>({
      query: (portalKey) => `/portals/${portalKey}/webhooks`,
      transformResponse: (response: { data: Webhook[] } | Webhook[]) =>
        Array.isArray(response) ? response : response.data,
      providesTags: (_result, _error, portalKey) => [{ type: "Webhook", id: portalKey }],
    }),

    createWebhook: builder.mutation<
      Webhook,
      {
        portalKey: string;
        name: string;
        url: string;
        method?: "POST" | "PUT";
        headers?: Record<string, string>;
        events: WebhookEvent[];
        isActive?: boolean;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/webhooks`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Webhook", id: portalKey }],
    }),

    updateWebhook: builder.mutation<
      Webhook,
      {
        portalKey: string;
        webhookId: string;
        body: Partial<{
          name: string;
          url: string;
          method: "POST" | "PUT";
          headers: Record<string, string>;
          events: WebhookEvent[];
          isActive: boolean;
        }>;
      }
    >({
      query: ({ portalKey, webhookId, body }) => ({
        url: `/portals/${portalKey}/webhooks/${webhookId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Webhook", id: portalKey }],
    }),

    deleteWebhook: builder.mutation<void, { portalKey: string; webhookId: string }>({
      query: ({ portalKey, webhookId }) => ({
        url: `/portals/${portalKey}/webhooks/${webhookId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Webhook", id: portalKey }],
    }),

    getWebhookLogs: builder.query<
      PaginatedResponse<WebhookDeliveryLog>,
      { portalKey: string; webhookId: string; page?: number; limit?: number }
    >({
      query: ({ portalKey, webhookId, ...params }) => ({
        url: `/portals/${portalKey}/webhooks/${webhookId}/logs`,
        params,
      }),
    }),

    testWebhook: builder.mutation<
      { success: boolean; responseCode?: number; error?: string },
      { portalKey: string; webhookId: string }
    >({
      query: ({ portalKey, webhookId }) => ({
        url: `/portals/${portalKey}/webhooks/${webhookId}/test`,
        method: "POST",
      }),
    }),

    resetWebhookFailures: builder.mutation<
      { success: boolean },
      { portalKey: string; webhookId: string }
    >({
      query: ({ portalKey, webhookId }) => ({
        url: `/portals/${portalKey}/webhooks/${webhookId}/reset`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Webhook", id: portalKey }],
    }),

    // ── HubSpot ──
    getHubSpotConfig: builder.query<HubSpotConfig, string>({
      query: (portalKey) => `/portals/${portalKey}/hubspot/config`,
      providesTags: (_result, _error, portalKey) => [{ type: "HubSpotConfig", id: portalKey }],
    }),

    saveHubSpotConfig: builder.mutation<
      { success: boolean },
      {
        portalKey: string;
        token?: string;
        fieldMapping?: Record<string, string>;
        syncFilters?: HubSpotSyncFilter[];
        pushEnabled?: boolean;
        noteOwnerEmail?: string;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/hubspot/config`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "HubSpotConfig", id: portalKey },
      ],
    }),

    deleteHubSpotConfig: builder.mutation<{ success: boolean }, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/hubspot/config`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, portalKey) => [{ type: "HubSpotConfig", id: portalKey }],
    }),

    syncHubSpotTickets: builder.mutation<HubSpotSyncResult, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/hubspot/sync`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, portalKey) => [
        { type: "HubSpotConfig", id: portalKey },
        { type: "WorkItem", id: portalKey },
      ],
    }),

    importCsv: builder.mutation<
      { created: number; skipped: number; errors: string[] },
      { portalKey: string; file: File; fieldMapping: Record<string, string> }
    >({
      query: ({ portalKey, file, fieldMapping }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("fieldMapping", JSON.stringify(fieldMapping));
        return {
          url: `/portals/${portalKey}/items/import-csv`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "WorkItem", id: portalKey }],
    }),

    previewJiraCsv: builder.mutation<
      {
        totalRows: number;
        statuses: { name: string; category: string; exists: boolean }[];
        requestTypes: { name: string; workType: string; exists: boolean }[];
        priorities: { jira: string; mapped: string; count: number }[];
        assignees: { name: string; matched: boolean; count: number }[];
        reporters: { name: string; isAgent: boolean; count: number }[];
        commentCount: number;
      },
      { portalKey: string; file: File }
    >({
      query: ({ portalKey, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/portals/${portalKey}/items/preview-jira-csv`,
          method: "POST",
          body: formData,
        };
      },
    }),

    importJiraCsv: builder.mutation<
      {
        totalRows: number;
        imported: number;
        skipped: number;
        errors: string[];
        commentsImported: number;
        contactsCreated: number;
        requestTypesCreated: number;
        statusesCreated: number;
      },
      { portalKey: string; file: File }
    >({
      query: ({ portalKey, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/portals/${portalKey}/items/import-jira-csv`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "WorkItem", id: portalKey }],
    }),

    testHubSpotConnection: builder.mutation<
      { properties: HubSpotProperty[] },
      { portalKey: string; token: string }
    >({
      query: ({ portalKey, token }) => ({
        url: `/portals/${portalKey}/hubspot/test`,
        method: "POST",
        body: { token },
      }),
    }),

    getHubSpotProperties: builder.query<{ properties: HubSpotProperty[] }, string>({
      query: (portalKey) => `/portals/${portalKey}/hubspot/properties`,
      providesTags: (_result, _error, portalKey) => [{ type: "HubSpotConfig", id: portalKey }],
    }),

    // ── SharePoint ──
    getSharePointConfig: builder.query<SharePointConfig, string>({
      query: (portalKey) => `/portals/${portalKey}/sharepoint/config`,
      providesTags: (_result, _error, portalKey) => [
        { type: "SharePointConfig" as never, id: portalKey },
      ],
    }),

    saveSharePointConfig: builder.mutation<
      { success: boolean },
      {
        portalKey: string;
        azureTenantId: string;
        azureClientId: string;
        azureClientSecret?: string;
        siteUrl: string;
        siteId?: string;
        driveId?: string;
        driveName?: string;
        folderId?: string | null;
        folderPath?: string | null;
        categoryId?: string | null;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/sharepoint/config`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "SharePointConfig" as never, id: portalKey },
      ],
    }),

    deleteSharePointConfig: builder.mutation<{ success: boolean }, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/sharepoint/config`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, portalKey) => [
        { type: "SharePointConfig" as never, id: portalKey },
      ],
    }),

    testSharePointConnection: builder.mutation<
      { siteId: string; siteName: string },
      {
        portalKey: string;
        azureTenantId: string;
        azureClientId: string;
        azureClientSecret: string;
        siteUrl: string;
      }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/sharepoint/test`,
        method: "POST",
        body,
      }),
    }),

    getSharePointDrives: builder.query<{ drives: SharePointDrive[] }, string>({
      query: (portalKey) => `/portals/${portalKey}/sharepoint/drives`,
    }),

    getSharePointFolders: builder.query<
      { folders: SharePointFolder[] },
      { portalKey: string; driveId: string; folderId?: string }
    >({
      query: ({ portalKey, driveId, folderId }) => {
        const params = folderId ? `?folderId=${folderId}` : "";
        return `/portals/${portalKey}/sharepoint/drives/${driveId}/folders${params}`;
      },
    }),

    previewSharePointFiles: builder.mutation<{ files: SharePointFilePreview[] }, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/sharepoint/sync/preview`,
        method: "POST",
      }),
    }),

    syncSharePointDocuments: builder.mutation<
      SharePointSyncResult,
      { portalKey: string; fileIds?: string[] }
    >({
      query: ({ portalKey, fileIds }) => ({
        url: `/portals/${portalKey}/sharepoint/sync`,
        method: "POST",
        body: fileIds ? { fileIds } : {},
        timeout: 300_000,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "SharePointConfig" as never, id: portalKey },
        { type: "KbArticle", id: portalKey },
      ],
    }),

    // ──────────────────────────── AI Agent ────────────────────────────

    getAiAgent: builder.query<AiAgent | null, string>({
      query: (portalKey) => `/portals/${portalKey}/ai-agent`,
      transformResponse: (response: { data: AiAgent | null }) => response.data,
      providesTags: (_result, _error, portalKey) => [{ type: "AiAgent", id: portalKey }],
    }),

    upsertAiAgent: builder.mutation<AiAgent, { portalKey: string; data: Partial<AiAgent> }>({
      query: ({ portalKey, data }) => ({
        url: `/portals/${portalKey}/ai-agent`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response: { data: AiAgent }) => response.data,
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AiAgent", id: portalKey },
        { type: "AiAgent", id: "LIST" },
      ],
    }),

    deleteAiAgent: builder.mutation<void, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/ai-agent`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, portalKey) => [
        { type: "AiAgent", id: portalKey },
        { type: "AiAgent", id: "LIST" },
      ],
    }),

    getAgentKbCategories: builder.query<AiAgentKbCategory[], string>({
      query: (portalKey) => `/portals/${portalKey}/ai-agent/kb-categories`,
      transformResponse: (response: { data: AiAgentKbCategory[] }) => response.data,
      providesTags: (_result, _error, portalKey) => [{ type: "AiAgent", id: `kb-${portalKey}` }],
    }),

    setAgentKbCategories: builder.mutation<
      AiAgentKbCategory[],
      { portalKey: string; categoryIds: string[] }
    >({
      query: ({ portalKey, categoryIds }) => ({
        url: `/portals/${portalKey}/ai-agent/kb-categories`,
        method: "PUT",
        body: { categoryIds },
      }),
      transformResponse: (response: { data: AiAgentKbCategory[] }) => response.data,
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AiAgent", id: `kb-${portalKey}` },
      ],
    }),

    getAgentSkills: builder.query<AiAgentSkill[], string>({
      query: (portalKey) => `/portals/${portalKey}/ai-agent/skills`,
      transformResponse: (response: { data: AiAgentSkill[] }) => response.data,
      providesTags: (_result, _error, portalKey) => [
        { type: "AiAgent", id: `skills-${portalKey}` },
      ],
    }),

    setAgentSkills: builder.mutation<
      AiAgentSkill[],
      {
        portalKey: string;
        skills: { skillId: string; isEnabled: boolean; config?: Record<string, unknown> }[];
      }
    >({
      query: ({ portalKey, skills }) => ({
        url: `/portals/${portalKey}/ai-agent/skills`,
        method: "PUT",
        body: { skills },
      }),
      transformResponse: (response: { data: AiAgentSkill[] }) => response.data,
      invalidatesTags: (_result, _error, { portalKey }) => [
        { type: "AiAgent", id: `skills-${portalKey}` },
      ],
    }),

    listAllAiAgents: builder.query<AiAgent[], void>({
      query: () => `/portals/ai-playground`,
      transformResponse: (response: { data: AiAgent[] }) => response.data,
      providesTags: [{ type: "AiAgent", id: "LIST" }],
    }),

    listSkillProviders: builder.query<AiSkillProvider[], void>({
      query: () => `/portals/ai-playground/skill-providers`,
      transformResponse: (response: { data: AiSkillProvider[] }) => response.data,
      providesTags: [{ type: "AiSkillProvider", id: "LIST" }],
    }),

    listAvailableSkills: builder.query<AiSkill[], { providerId?: string } | void>({
      query: (params) => {
        const providerId = params && "providerId" in params ? params.providerId : undefined;
        return `/portals/ai-playground/skills${providerId ? `?providerId=${providerId}` : ""}`;
      },
      transformResponse: (response: { data: AiSkill[] }) => response.data,
      providesTags: [{ type: "AiSkillProvider", id: "SKILLS" }],
    }),

    startAiChatSession: builder.mutation<{ sessionId: string; welcomeMessage: string }, string>({
      query: (portalKey) => ({
        url: `/portals/${portalKey}/ai-agent/chat/new`,
        method: "POST",
      }),
      transformResponse: (response: { data: { sessionId: string; welcomeMessage: string } }) =>
        response.data,
    }),

    sendAiChat: builder.mutation<
      AiChatResponse,
      { portalKey: string; sessionId: string; message: string }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/ai-agent/chat`,
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: AiChatResponse }) => response.data,
      // Refresh the history list and this transcript. Deliberately NOT the
      // agent list: that query is mounted for the whole page and now carries
      // the counting subqueries, and the count can only change on the first
      // user message of a session — the caller invalidates it just then.
      invalidatesTags: (_result, _error, { portalKey, sessionId }) => [
        { type: "AiConversation", id: `list-${portalKey}` },
        { type: "AiConversation", id: sessionId },
      ],
    }),

    listAiConversations: builder.query<
      { conversations: AiConversationSummary[]; total: number },
      { portalKey: string; page?: number; limit?: number }
    >({
      query: ({ portalKey, page = 1, limit = 25 }) =>
        `/portals/${portalKey}/ai-agent/conversations?page=${page}&limit=${limit}`,
      transformResponse: (response: { data: AiConversationSummary[]; total: number }) => ({
        conversations: response.data,
        total: response.total,
      }),
      providesTags: (_result, _error, { portalKey }) => [
        { type: "AiConversation", id: `list-${portalKey}` },
      ],
    }),

    getAiConversation: builder.query<AiChatMessage[], { portalKey: string; sessionId: string }>({
      query: ({ portalKey, sessionId }) =>
        `/portals/${portalKey}/ai-agent/conversations/${sessionId}`,
      transformResponse: (response: { data: AiChatMessage[] }) => response.data,
      providesTags: (_result, _error, { sessionId }) => [{ type: "AiConversation", id: sessionId }],
    }),

    // Forms
    getPublishedForm: builder.query<{ data: Form }, string>({
      query: (formId) => `/forms/${formId}`,
      providesTags: (_result, _error, formId) => [{ type: "Form", id: `fill-${formId}` }],
    }),

    getForms: builder.query<{ data: Form[]; total: number }, string>({
      query: (portalKey) => `/portals/${portalKey}/forms`,
      providesTags: (_result, _error, portalKey) => [{ type: "Form", id: portalKey }],
    }),

    getForm: builder.query<{ data: Form }, { portalKey: string; formId: string }>({
      query: ({ portalKey, formId }) => `/portals/${portalKey}/forms/${formId}`,
      providesTags: (_result, _error, { formId }) => [{ type: "Form", id: formId }],
    }),

    createForm: builder.mutation<
      { data: Form },
      { portalKey: string; name: string; description?: string }
    >({
      query: ({ portalKey, ...body }) => ({
        url: `/portals/${portalKey}/forms`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Form", id: portalKey }],
    }),

    updateForm: builder.mutation<
      { data: Form },
      {
        portalKey: string;
        formId: string;
        name?: string;
        description?: string | null;
        status?: FormStatus;
      }
    >({
      query: ({ portalKey, formId, ...body }) => ({
        url: `/portals/${portalKey}/forms/${formId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { portalKey, formId }) => [
        { type: "Form", id: portalKey },
        { type: "Form", id: formId },
      ],
    }),

    deleteForm: builder.mutation<void, { portalKey: string; formId: string }>({
      query: ({ portalKey, formId }) => ({
        url: `/portals/${portalKey}/forms/${formId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { portalKey }) => [{ type: "Form", id: portalKey }],
    }),

    createFormField: builder.mutation<
      { data: FormField },
      {
        portalKey: string;
        formId: string;
        label: string;
        fieldType: FormFieldType;
        options?: string[];
        isRequired?: boolean;
        position?: number;
      }
    >({
      query: ({ portalKey, formId, ...body }) => ({
        url: `/portals/${portalKey}/forms/${formId}/fields`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { formId }) => [{ type: "Form", id: formId }],
    }),

    updateFormField: builder.mutation<
      { data: FormField },
      {
        portalKey: string;
        formId: string;
        fieldId: string;
        label?: string;
        fieldType?: FormFieldType;
        options?: string[];
        isRequired?: boolean;
        position?: number;
      }
    >({
      query: ({ portalKey, formId, fieldId, ...body }) => ({
        url: `/portals/${portalKey}/forms/${formId}/fields/${fieldId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { formId }) => [{ type: "Form", id: formId }],
    }),

    deleteFormField: builder.mutation<void, { portalKey: string; formId: string; fieldId: string }>(
      {
        query: ({ portalKey, formId, fieldId }) => ({
          url: `/portals/${portalKey}/forms/${formId}/fields/${fieldId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { formId }) => [{ type: "Form", id: formId }],
      },
    ),

    reorderFormFields: builder.mutation<
      void,
      { portalKey: string; formId: string; orderedFieldIds: string[] }
    >({
      query: ({ portalKey, formId, orderedFieldIds }) => ({
        url: `/portals/${portalKey}/forms/${formId}/fields/reorder`,
        method: "POST",
        body: { orderedFieldIds },
      }),
      invalidatesTags: (_result, _error, { formId }) => [{ type: "Form", id: formId }],
    }),

    submitFormResponse: builder.mutation<
      { data: FormResponse },
      {
        formId: string;
        answers: { fieldId: string; value: string }[];
        ticketKey?: string;
        workItemId?: string;
        requestTypeId?: string;
        attachmentIds?: string[];
      }
    >({
      query: ({ formId, answers, ticketKey, workItemId, requestTypeId, attachmentIds }) => ({
        url: `/forms/${formId}/responses`,
        method: "POST",
        body: { answers, ticketKey, workItemId, requestTypeId, attachmentIds },
      }),
      invalidatesTags: (_result, _error, { formId }) => [{ type: "FormResponse", id: formId }],
    }),

    getFormResponses: builder.query<
      { data: FormResponse[]; total: number },
      { portalKey: string; formId: string }
    >({
      query: ({ portalKey, formId }) => `/portals/${portalKey}/forms/${formId}/responses`,
      providesTags: (_result, _error, { formId }) => [{ type: "FormResponse", id: formId }],
    }),
  }),
});

export const {
  useGetBinPolicyQuery,
  useGetPortalsQuery,
  useGetMyPortalsQuery,
  useGetPortalQuery,
  useCreatePortalMutation,
  useCopyPortalMutation,
  useUpdatePortalMutation,
  useDeletePortalMutation,
  useRestorePortalMutation,
  usePermanentlyDeletePortalMutation,
  useArchivePortalMutation,
  useStarPortalMutation,
  useUploadPortalLogoMutation,
  useDeletePortalLogoMutation,
  useGetPortalMembersQuery,
  useAddPortalMemberMutation,
  useBulkAddPortalMembersMutation,
  useUpdatePortalMemberMutation,
  useRemovePortalMemberMutation,
  useGetMyPortalPermissionsQuery,
  useGetMyTicketAccessQuery,
  useGetPortalApproversQuery,
  useGetMemberHistoryQuery,
  useGetWorkItemApproversQuery,
  useRespondWorkItemApprovalMutation,
  useAddWorkItemApproversMutation,
  useRemoveWorkItemApproverMutation,
  useGetWorkItemsQuery,
  useGetWorkItemQuery,
  useCreateWorkItemMutation,
  useUpdateWorkItemMutation,
  useDeleteWorkItemMutation,
  useGetWorkItemsByBoardQuery,
  useMoveWorkItemMutation,
  useGetMovePortalOptionsQuery,
  useLazyGetMovePortalOptionsQuery,
  useMoveItemToPortalMutation,
  useGetBoardConfigQuery,
  useUpdateBoardConfigMutation,
  useGetWorkItemCommentsQuery,
  useAddWorkItemCommentMutation,
  useUpdateWorkItemCommentMutation,
  useUpdateWorkItemCommentBodyMutation,
  useDeleteWorkItemCommentMutation,

  useGetPortalWorkflowsQuery,
  useGetAllPortalItemsQuery,
  useGetWorkItemCountsQuery,
  useGetMyWorkItemsQuery,
  useGetAllTicketsQuery,
  // Portal Email Config
  useGetPortalEmailConfigQuery,
  useUpdatePortalEmailConfigMutation,
  // Notification Preferences
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  // Notification Logs
  useGetNotificationLogsQuery,
  // Dashboard
  useGetPortalsDashboardQuery,
  // Request Types
  useGetRequestTypesQuery,
  useCreateRequestTypeMutation,
  useUpdateRequestTypeMutation,
  useDeleteRequestTypeMutation,
  useRenameWorkTypeMutation,
  useDeleteWorkTypeMutation,
  // Work Type Settings
  useGetWorkTypeSettingsQuery,
  useUpdateWorkTypeSettingsMutation,
  // Portal Groups
  useGetPortalGroupsQuery,
  useCreatePortalGroupMutation,
  useUpdatePortalGroupMutation,
  useDeletePortalGroupMutation,
  useUpdateGroupRequestTypesMutation,
  useReorderPortalGroupMutation,
  useReorderRequestTypeMutation,
  // Queues
  useGetQueuesQuery,
  useGetQueueItemsQuery,
  useCreateQueueMutation,
  useUpdateQueueMutation,
  useDeleteQueueMutation,
  // Saved Views
  useGetSavedViewsQuery,
  useCreateSavedViewMutation,
  useUpdateSavedViewMutation,
  useDeleteSavedViewMutation,

  // Portal Roles
  useGetPortalRolesQuery,
  useCreatePortalRoleMutation,
  useUpdatePortalRoleMutation,
  useDeletePortalRoleMutation,
  // Workflow mutations
  useCreateWorkflowMutation,
  useGetWorkflowUsageQuery,
  useGetSlaPolicyUsageQuery,
  useDeleteWorkflowMutation,
  useCreateWorkflowStatusMutation,
  useUpdateWorkflowStatusMutation,
  useReorderWorkflowStatusMutation,
  useDeleteWorkflowStatusMutation,
  useLazyGetStatusTicketCountQuery,
  useCreateWorkflowTransitionMutation,
  useDeleteWorkflowTransitionMutation,
  useCreateTransitionMutation,
  useUpdateTransitionMutation,
  useDeleteTransitionMutation,
  useListTransitionRulesQuery,
  useCreateTransitionRuleMutation,
  useUpdateTransitionRuleMutation,
  useDeleteTransitionRuleMutation,
  useListStatusEntryRulesQuery,
  useCreateStatusEntryRuleMutation,
  useUpdateStatusEntryRuleMutation,
  useDeleteStatusEntryRuleMutation,
  useBulkSetStatusPositionsMutation,
  useGetAvailableTransitionsQuery,
  // Unlinked attachment upload/delete
  useUploadPortalAttachmentMutation,
  useDeletePortalAttachmentMutation,
  // Attachments
  useGetWorkItemAttachmentsQuery,
  useUploadWorkItemAttachmentMutation,
  useDeleteWorkItemAttachmentMutation,
  // Tags
  useGetPortalTagsQuery,
  // History & Links
  useGetWorkItemHistoryQuery,
  useGetWorkItemLinksQuery,
  useCreateWorkItemLinkMutation,
  useDeleteWorkItemLinkMutation,
  // Children (Subtasks)
  useGetWorkItemChildrenQuery,
  // Watchers
  useLazyGetWorkItemAiReplyQuery,
  useGetWorkItemSummaryQuery,
  useGetWorkItemWatchersQuery,
  useAddWorkItemWatcherMutation,
  useRemoveWorkItemWatcherMutation,
  // Bulk Operations
  useBulkTransitionWorkItemsMutation,
  // Custom Fields
  useGetCustomFieldsQuery,
  useGetResolutionRequirementsQuery,
  useLazyGetResolutionRequirementsQuery,
  useCreateCustomFieldMutation,
  useUpdateCustomFieldMutation,
  useDeleteCustomFieldMutation,
  // Recurring Tickets
  useListRecurringTicketsQuery,
  useCreateRecurringTicketMutation,
  useUpdateRecurringTicketMutation,
  useDeleteRecurringTicketMutation,
  usePauseRecurringTicketMutation,
  useResumeRecurringTicketMutation,
  useRunRecurringTicketNowMutation,
  // SLA Policies
  useGetSlaPoliciesQuery,
  useGetWorkItemSlaQuery,
  useCreateSlaPolicyMutation,
  useUpdateSlaPolicyMutation,
  useDeleteSlaPolicyMutation,
  // Business Hours
  useGetBusinessHoursQuery,
  useCreateBusinessHoursMutation,
  useUpdateBusinessHoursMutation,
  useDeleteBusinessHoursMutation,
  // Automation Rules
  useGetAutomationRulesQuery,
  useCreateAutomationRuleMutation,
  useUpdateAutomationRuleMutation,
  useDeleteAutomationRuleMutation,
  // Macros
  useGetMacrosQuery,
  useCreateMacroMutation,
  useUpdateMacroMutation,
  useDeleteMacroMutation,
  useApplyMacroMutation,
  // Reports
  useGetCreatedVsResolvedQuery,
  useGetSlaComplianceQuery,
  useGetAgentPerformanceQuery,
  useGetRequestTypeBreakdownQuery,
  useGetResolutionTimeQuery,
  // Report Exports
  useLazyExportCreatedVsResolvedQuery,
  useLazyExportSlaComplianceQuery,
  useLazyExportAgentPerformanceQuery,
  useLazyExportRequestTypesQuery,
  useLazyExportResolutionTimeQuery,
  useLazyExportCustomReportQuery,
  // Custom Reports
  useGetCustomReportsQuery,
  useGetCustomReportByIdQuery,
  useCreateCustomReportMutation,
  useUpdateCustomReportMutation,
  useDeleteCustomReportMutation,
  useReorderCustomReportsMutation,
  useExecuteCustomReportMutation,
  // Scheduled Reports
  useGetScheduledReportsQuery,
  useGetScheduledReportByIdQuery,
  useCreateScheduledReportMutation,
  useUpdateScheduledReportMutation,
  useDeleteScheduledReportMutation,
  useRunScheduledReportNowMutation,
  useGetScheduledReportRunsQuery,
  usePreviewScheduledReportMutation,
  useSendScheduledReportTestMutation,
  // Report Builder
  useGenerateReportConfigMutation,
  useExecuteReportConfigMutation,
  useExecuteReportConfigCachedQuery,
  // Report Settings
  useGetReportSettingsQuery,
  useUpdateReportVisibilityMutation,
  useReorderReportsMutation,
  // Instance-level Fields
  useGetInstanceFieldsQuery,
  useCreateInstanceFieldMutation,
  useUpdateInstanceFieldMutation,
  useDeleteInstanceFieldMutation,
  // Knowledge Base
  useGetKbCategoriesQuery,
  useCreateKbCategoryMutation,
  useUpdateKbCategoryMutation,
  useDeleteKbCategoryMutation,
  useGetKbArticlesQuery,
  useGetKbArticleQuery,
  useCreateKbArticleMutation,
  useUpdateKbArticleMutation,
  useDeleteKbArticleMutation,
  usePublishKbArticleMutation,
  useUploadKbImageMutation,
  useSubmitKbFeedbackMutation,
  useSearchKbArticlesQuery,
  useSuggestKbArticlesQuery,
  // CSAT
  useGetCsatSettingsQuery,
  useUpdateCsatSettingsMutation,
  useGetCsatResponsesQuery,
  useGetCsatStatsQuery,
  useGetNpsStatsQuery,
  // Help Widgets
  useGetHelpWidgetsQuery,
  useCreateHelpWidgetMutation,
  useUpdateHelpWidgetMutation,
  useDeleteHelpWidgetMutation,
  useRegenerateHelpWidgetKeyMutation,
  // Inbox
  useGetInboxQuery,
  // Archive / Unarchive / Restore
  useArchiveWorkItemMutation,
  useUnarchiveWorkItemMutation,
  useBulkArchiveWorkItemsMutation,
  useBulkDeleteWorkItemsMutation,
  useBulkUnarchiveWorkItemsMutation,
  useGetArchivedWorkItemsQuery,
  useGetDeletedWorkItemsQuery,
  useBulkRestoreWorkItemsMutation,
  useRestoreAllWorkItemsMutation,
  useEmptyBinMutation,
  useSplitWorkItemMutation,
  useMergeWorkItemMutation,
  useUnmergeWorkItemMutation,
  // Webhooks
  useGetWebhooksQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useGetWebhookLogsQuery,
  useTestWebhookMutation,
  useResetWebhookFailuresMutation,
  // HubSpot
  useGetHubSpotConfigQuery,
  useSaveHubSpotConfigMutation,
  useDeleteHubSpotConfigMutation,
  useSyncHubSpotTicketsMutation,
  useImportCsvMutation,
  usePreviewJiraCsvMutation,
  useImportJiraCsvMutation,
  useTestHubSpotConnectionMutation,
  useLazyGetHubSpotPropertiesQuery,
  // SharePoint
  useGetSharePointConfigQuery,
  useSaveSharePointConfigMutation,
  useDeleteSharePointConfigMutation,
  useTestSharePointConnectionMutation,
  useGetSharePointDrivesQuery,
  useLazyGetSharePointDrivesQuery,
  useLazyGetSharePointFoldersQuery,
  usePreviewSharePointFilesMutation,
  useSyncSharePointDocumentsMutation,
  // AI Agent
  useGetAiAgentQuery,
  useUpsertAiAgentMutation,
  useDeleteAiAgentMutation,
  useGetAgentKbCategoriesQuery,
  useSetAgentKbCategoriesMutation,
  useGetAgentSkillsQuery,
  useSetAgentSkillsMutation,
  useListAllAiAgentsQuery,
  useListSkillProvidersQuery,
  useListAvailableSkillsQuery,
  useStartAiChatSessionMutation,
  useSendAiChatMutation,
  useListAiConversationsQuery,
  useGetAiConversationQuery,
  useLazyGetAiConversationQuery,
  // Forms
  useGetPublishedFormQuery,
  useGetFormsQuery,
  useGetFormQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
  useCreateFormFieldMutation,
  useUpdateFormFieldMutation,
  useDeleteFormFieldMutation,
  useReorderFormFieldsMutation,
  useSubmitFormResponseMutation,
  useGetFormResponsesQuery,
  // Assignment Matrix
  useGetAssignmentMatrixRulesQuery,
  useCreateAssignmentMatrixRuleMutation,
  useUpdateAssignmentMatrixRuleMutation,
  useDeleteAssignmentMatrixRuleMutation,
  useUpdateShiftHandoffMutation,
  useGetAssignmentMatrixCoverageQuery,
} = portalsApi;
